import { cellOrOverflow } from '@/lib/coldroomPlacement'
import { useState, useMemo, useDeferredValue, useEffect, useRef } from 'react'
import { StatusPill, Button } from '@/components/ui'
import { MissingCardRequests } from '@/components/RateCardHandoff'
import { POLL } from '@/lib/pollTiers'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams, Link } from 'react-router-dom'
import ModuleTabBar, { type ModuleTab } from '@/components/ModuleTabBar'
import Modal from '@/components/Modal'
import { useUrlState, oneOf } from '@/hooks/useUrlState'
import { useReturnTo } from '@/lib/returnTo'
import { useCanOpenModule } from '@/hooks/useCanOpenModule'
import { RunGrowerLink, RunQcLink } from './runBackLinks'
import { supabase } from '@/lib/supabase'
import { sastTodayYMD, fmtDateZA } from '@/lib/dates'
import type { Run, Delivery } from '@/types/database'
import { Package, ChevronDown, Plus, Check, ChevronRight, FlaskConical, AlertTriangle, Share2, ShieldCheck, Copy, Ticket, Trash2, Download, X, Search, ArrowLeftRight, FileText, Scale, Combine, LayoutGrid, Truck } from 'lucide-react'
import PageHeader from '@/components/PageHeader'
import { AddDeliveryToRunButton } from './AddDeliveryToRunButton'
import ChangeRunFruitModal from './ChangeRunFruitModal'
import { canChangeRunFruit } from '@/lib/canChangeRunFruit'
import SubmitOverlay from '@/components/SubmitOverlay'
import MutationOverlays from '@/components/MutationOverlays'
import TeachingEmptyState from '@/components/TeachingEmptyState'
import clsx from 'clsx'
import { checkVerification } from '@/lib/qcCoverage'
import type { QcVerificationMetrics } from '@/hooks/useQcVerificationMetrics'
import { useDefectLabelMap } from '@/hooks/useQcDefectTypes'
import { toCSV, downloadCSV, safeFilename } from '@/lib/csv'
import { computeShrinkage, useShrinkageRates, shrinkageColour } from '@/lib/shrinkage'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { usePermissions } from '@/lib/usePermissions'
import { isAdmin, canAdminPallets, isManagerOrAbove, isSuperAdmin, tenantRoleOf } from '@/lib/roles'
import { usePlatformView, useSupportView } from '@/lib/platformView'
import { isExternalReceiptRun } from '@/lib/externalReceiptPallets'
import MovePalletDialog from '@/components/MovePalletDialog'
import QueryStateGate from '@/components/QueryStateGate'
import BalancePackoutDialog from '@/components/BalancePackoutDialog'
import PalletTable, { PalletViewToggle } from '@/components/PalletTable'
import PalletSummaryInfo from '@/components/PalletSummaryInfo'
import { updateWithVersion, isStaleWriteError } from '@/lib/optimistic'
import { RunActionButtons } from './RunActionButtons'
import { RunFedLine } from './RunFedLine'
import { RunPackagingCard } from './RunPackagingCard'
import { RunDeliveryLink } from './RunDeliveryLink'
import { RunOpenItems } from './RunOpenItems'
import { useIsPlatformAdminState } from '@/lib/useIsPlatformAdmin'
import { VerifyQcGate } from './VerifyQcGate'
import { RunCompleteDone, type RunCompleted } from './RunCompleteDone'
import { canArchiveRun, type RunStatus } from '@/lib/runStatus'
import { BinTipSource } from '@/components/BinTipSource'
import { RunLotsSection } from './RunLots'
import { binSourceOf } from '@/lib/tipSource'
import { prepackedStagedSummary } from '@/lib/runLots'
import { fetchAllPaged } from '@/lib/fetchAllPaged'
import { RECEIPTS_WAITING_PATH } from '@/lib/palletReceiptsFilter'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import { readState } from '@/lib/readState'
import WidgetRetry from '@/components/WidgetRetry'
import { useActiveTenant } from '@/lib/useActiveTenant'
import { anyFieldOr, searchPattern, filtersActive, packoutPair, runCells, fmtPct, offersRunDelete, canDeletePlacedRun, runStatusesFor, RUN_STATUS_CHIPS, registerPollMs } from '@/lib/runsRegister'
import { useRunsLinkFilters, useNewRunLink } from './useRunsLinkFilters'
import { useWaitingDollies } from './useWaitingDollies'
import { useRunLifecycle, type RunLifecycle } from './useRunLifecycle'
import { runsKpis, kpiWindowStart, kpiOrFilter } from '@/lib/runsKpis'
import { RunsKpiStrip } from './RunsKpiStrip'
import { removeTokenAllocation, isRlsRefusal, TOKEN_RANGE_ROLE_REFUSAL } from './runTokenRanges'
import { fetchRunDefectTally } from './runDefectTally'
import { fetchAllVerifiedRuns, verifiedRunsCsv, verifiedRunsFileName } from './runsExport'
import { PackoutTiles, RunChips, RunsTable, runStatusLabel } from './RunsTable'

type RegisterRow = Run & { pack_lines?: { name: string | null } | null }
import { ValidationError } from '@/lib/errorReporter'
import { friendlyError, throwFriendly } from '@/lib/friendlyError'
import { useCanStartRun, RUN_CREATE_DOMAIN, RUN_CREATE_REFUSED } from '@/hooks/useCanStartRun'
import { readErrorText } from '@/lib/readError'
import { usePersistedState } from '@/hooks/usePersistedState'
import { confirmDialog, promptDialog, alertDialog } from '@/components/Dialogs'
import { canUndoTip as canUndoTipRule } from '@/lib/canUndoTip'
import { undoTipMessage } from '@/lib/undoTipMessage'
import { confirmUndoTip } from '@/components/confirmUndoTip'
import { useGrowers, usePucs, useGrowerTree } from '@/hooks/useGrowerData'
import { groupVarietiesByFruit, fruitLabel, noPackableCode, type VarietyGroup } from '@/lib/runVarietyGroups'
import { useEntityFlowResolverByName } from '@/lib/entityFlow'
import { useActiveProductRules } from './usePalletPricing'
import { useProductionFlow, useProductionFlowState } from '@/lib/useProductionFlow'
import { useFlowManifest } from '@/hooks/useCommodityTemplates'
import { sourceChip, rejectBreakdownSources } from '@/lib/rejectSource'
import { useCommodities, useCommodityResolver, useCommodityPackFormats } from '@/hooks/useCommodities'
import { useDuoPackFormats } from '@/hooks/useDuoPackFormats'
import { formatGradeLabel } from '@/lib/gradeLabel'
import { withTimeout } from '@/lib/saveTimeout'
import { useSaveDb, ensureSaveDb } from '@/lib/useSaveDb'
import RunVarietyPanel, { RunVarietyMismatchBanner } from './RunVarietyPanel'
import RunFlowGroups from './RunFlowGroups'
import RunFlowLinks from './RunFlowLinks'
import StaleBulkSortBanner from './StaleBulkSortBanner'
import RunMap from './RunMap'
import { Money, fmtMoneyAuto } from '@/lib/money'
import { useWhatsAppStatus } from '@/hooks/useWhatsAppStatus'
import { WhatsAppPausedNote } from '@/components/WhatsAppPausedNote'
import { notifyGrower } from '@/lib/whatsappStatus'
import { DELIVERY_STATUS_LABELS } from '@/lib/deliveryColumns'
import { useLinkedRunsOn, useRunGrains, grainFor } from '@/hooks/useLinkedRuns'
import { newRunVariety, MIXED_VARIETY, countOnce } from '@/lib/linkedRuns'
import LinkedRunFields, { EMPTY_LINKED_RUN, linkedRunInsertFields, type LinkedRunChoice } from './LinkedRunFields'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
// A delivery's status as the Deliveries screen words it, never the raw enum.
function deliveryStatusLabel(status: string): string {
  return DELIVERY_STATUS_LABELS[status] ?? status
}

// Pretty label for a bin's sort tuple — used to group the run picker.
function sortLabelFor(b: { size_code: string | null; grade: string | null }): string {
  if (b.size_code && b.grade) return `${b.size_code} · ${formatGradeLabel(b.grade)}`
  if (b.size_code)            return b.size_code
  if (b.grade)                return formatGradeLabel(b.grade)
  return '— No sort —'
}

interface StoredBin {
  bin_id:         string
  bin_number:     string
  grower:         string
  grower_id:      string | null
  variety:        string
  puc:            string | null
  born_date:      string
  net_weight_kg:  number
  coldroom_id:    string | null
  coldroom_grid:  string | null
  coldroom_overflow?: boolean | null
  size_code:      string | null
  grade:          string | null
  /** A bulk-sort pallet's crates and what is left (mig 20261289000000). */
  crate_count?:      number | null
  crates_remaining?: number | null
  source_run_id:  string
  runs:           { run_number: string; started_at: string } | null
  coldrooms:      { code: string } | null
}

interface Pallet {
  pallet_id:       string
  status:          string
  pallet_type:     string | null
  spec:            string | null
  grade:           string | null
  size_code:       string | null
  net_weight_kg:   number | null
  gross_weight_kg: number | null
  carton_count:    number | null
  target_market:   string | null
  destination:     string | null
  pack_type:       string | null
  mark:            string | null
  regime_code:     string | null
  pick_no:         string | null
  ssc_id:          string | null
  coldroom_grid:   string | null
  coldroom_overflow: boolean
  loading_bay_lane: string | null
  intake_no:       string | null
  consignment_no:  string | null
  packed_at:       string
  parent_pallet_id: string | null
  ppecb_consignment_id?: string | null
  truck_consignment_id?: string | null
  loading_mission_id?:   string | null
  run_id?:         string
  is_buildup?:     boolean
  // Decorated client-side from the FK lookups + pallet_lines embed below.
  line_count?:               number
  truck_consignment_number?: string | null
  ppecb_consignment_number?: string | null
  mission_number?:           string | null
  mission_dispatched_at?:    string | null
}

// A pallet can be SELECTED for a move only when this run is its HOME run (not a
// build-up slice from elsewhere — that pallet's home is a different run). The
// mig-810/820 RPC re-checks server-side.
function isSelectablePallet(p: Pallet): boolean {
  return !p.is_buildup
}

// Moving a dispatched / consignment / mission pallet needs the admin force
// override (the dialog requires an explicit confirm). Mirrors the server guard.
function palletNeedsForce(p: Pallet): boolean {
  return p.status === 'DISPATCHED'
    || !!p.ppecb_consignment_id
    || !!p.truck_consignment_id
    || !!p.loading_mission_id
}

interface TipEntry {
  tipping_id:           string
  bin_quantity:         number
  gross_weight_kg:      number
  net_weight_kg:        number
  tipped_at:            string
  // Source delivery's client_ref + DN number, embedded through the
  // tipping_log → deliveries FK so each tip card can show which
  // delivery it came from without an N+1 fetch.
  client_ref_no:        string | null
  delivery_note_number: string | null
  delivery_id:          string | null
  // Batch-sourced tips (berry force-cool lane): the source dolly. NULL for
  // delivery/bin-sourced tips. Drives the crate wording + the undo copy —
  // undoing a batch tip returns the batch to the Released pool (it does NOT
  // put crates back on the delivery; those were consumed at batch create).
  batch_id:             string | null
  batch_number:         string | null
  // Bin-sourced tip (retip of a stored bin). source_pallet_id names the
  // RECEIVED pallet the bin was stripped from (mig 20261019090000) — the
  // link that turns "Tip 3 · 1 bin" into a pallet the operator can find.
  bin_number:           string | null
  source_pallet_id:     string | null
  source_carton_count:  number | null
}

interface RejectEntry {
  reject_id:      string
  reject_source:  string | null
  kind:           'REJECT' | 'WASTE'
  weight_kg:      number
  bin_count:      number | null
  defect_reasons: string[] | null
  logged_at:      string
}

interface QCSummary {
  full_box:  number
  packer:    number
  titration: number
  warnings:  number
  avgBrix:   number | null
  avgAcid:   number | null
}

// Pallet status badge label + colour maps now live in @/lib/palletStatus
// (imported above) so Runs, Pallets and the shared PalletTable agree.

// REJECT_SOURCES / sourceChip / rejectBreakdownSources now live in
// @/lib/rejectSource (imported above), shared with Rejects.tsx so the citrus
// vs berry source handling can't drift between the two surfaces (M71).

// ---------------------------------------------------------------------------
// Run Detail View
// ---------------------------------------------------------------------------
type DetailTab = 'overview' | 'pallets' | 'tipping' | 'rejects' | 'qc'
// UF-13: the open tab lives in the address (?tab=). Tokens and Grower View are
// no longer tabs; an old ?tab=tokens or ?tab=grower falls back to Overview,
// where both now live.
const parseDetailTab = oneOf<DetailTab>('overview', 'pallets', 'tipping', 'rejects', 'qc')

interface TokenAllocation {
  allocation_id:   string
  run_id:          string
  order_reference: string | null
  token_prefix:    string
  range_start:     number
  range_end:       number
  notes:           string | null
  allocated_at:    string
}

function RunDetail({ run, onBack, lifecycle, done, onDismissDone }: {
  run: Run; onBack: () => void; lifecycle: RunLifecycle
  /** UF-05: set when Complete just landed on this run's page. */
  done?: RunCompleted | null; onDismissDone?: () => void
}) {
  // F06 UF-05: the Rejects tab links to the Log form, only for someone the route lets in.
  const canOpenRejects = useCanOpenModule('rejects')
  const [tab, setTab] = useUrlState<DetailTab>('tab', 'overview', parseDetailTab)
  const [growerOpen, setGrowerOpen] = useState(false)
  const me = useCurrentUser()
  // Titration (Brix/acid) is citrus QC — berry maturity lives in BlueberryQC,
  // so the titration tile is dropped from the count row for that flow.
  const manifest = useFlowManifest()
  const hasTitrationQc = manifest.hasTitrationQc
  // Berry (force-cool) rejects carry free-text sources (mig 1046) — resolve
  // them alongside the fixed citrus stages so a berry run's reject breakdown
  // and per-reject chips don't silently render blank (M71).
  const isBerry = manifest.usesForceCooling
  // Never on a received run: delete_run refuses it (it decides who is billed
  // for cold storage) — it closes by voiding its pallets (F07 RUNDEL).
  // Mirrors delete_run (F07 RUNLEVEL, mig 20261033200000): a run with a pallet
  // that ever stood in a coldroom is deleted only by a platform admin or
  // ADMIN / MANAGER of the business — a Super admin seat without that role is
  // refused, so the button is hidden (#2995 residual 8c).
  const platformView = usePlatformView()
  const supportView = useSupportView()
  const tenantRole = tenantRoleOf(me.data)
  const deleteCtx = {
    role: me.data?.role, tenantRole, runKind: run.run_kind, platformView, supportView,
  }
  const needsPlacedCheck = isSuperAdmin(me.data?.role) && !isExternalReceiptRun(run.run_kind) && !canDeletePlacedRun(deleteCtx) && !supportView
  const everPlacedQ = useQuery({
    queryKey: ['run-ever-placed', run.run_id],
    enabled: needsPlacedCheck,
    queryFn: async () => {
      const { data, error } = await supabase.from('pallets').select('pallet_id')
        .eq('run_id', run.run_id).or('status.eq.IN_COLDROOM,stocked_at.not.is.null').limit(1)
      if (error) throw error
      return (data ?? []).length > 0
    },
  })
  const canDelete = offersRunDelete({ ...deleteCtx, everPlaced: everPlacedQ.data })
  const meIsAdmin = isAdmin(me.data?.role)
  // Bulk "move to another run" — ADMIN/SUPER_ADMIN, or a custom role
  // holding pallets:override (the move RPC enforces the same, mig 20260707070653).
  const { can: canPerm, isCustomRole } = usePermissions()
  const canMovePallets = canAdminPallets(me.data?.role) || canPerm('pallets', 'override')
  const [selectedToMove, setSelectedToMove] = useState<Set<string>>(new Set())
  const [bulkMoving, setBulkMoving] = useState(false)
  // Pallets tab: cards (default) vs Excel-style column table. Persisted so
  // the operator's preference survives a detail round-trip.
  const [palletView, setPalletView] = usePersistedState<'cards' | 'table'>('kf49.runDetail.palletView', 'cards')
  function toggleMoveSelect(palletId: string) {
    setSelectedToMove(prev => {
      const next = new Set(prev)
      if (next.has(palletId)) next.delete(palletId)
      else next.add(palletId)
      return next
    })
  }
  // Untip allowed for whoever the undo_tipping RPC admits: tipping:edit
  // (mig 20261019150000) — scale operators hold it by default ("operator
  // clicked the wrong delivery, same shift"), and so can a custom role. Same
  // gate as the Tipping page; a fixed role list hid it from custom roles.
  const canUndoTip = canUndoTipRule(me.data?.role, canPerm, isCustomRole)
  // UF-07: Change fruit on a run that holds nothing yet. The RPC reads the
  // TENANT role, so a global SUPER_ADMIN without a membership is refused; a
  // platform admin in platform view is admitted.
  const [changingFruit, setChangingFruit] = useState(false)
  const offersChangeFruit = canChangeRunFruit({
    role: tenantRole, canEditRuns: canPerm('runs', 'edit'), run, inPlatformView: platformView,
  })
  const [editingStarted, setEditingStarted] = useState(false)
  const [editStartedDate, setEditStartedDate] = useState(run.started_at?.slice(0, 10) ?? '')
  const [balancing, setBalancing] = useState(false)
  const queryClient = useQueryClient()
  const saveDb = useSaveDb()

  const editStartedAt = useMutation({
    mutationFn: async () => {
      if (!editStartedDate) throw new ValidationError('Pick a date')
      const iso = `${editStartedDate}T12:00:00+02:00`
      try {
        await updateWithVersion('runs',
          { col: 'run_id', value: run.run_id },
          run.version,
          { started_at: iso, update_reason: 'admin back-date of started_at' },
        )
      } catch (e) {
        if (isStaleWriteError(e)) {
          throw new ValidationError(
            'This run was just modified by someone else. Refresh and re-enter the date.',
          )
        }
        throw e
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['runs'] })
      setEditingStarted(false)
    },
    onError: (e: Error) => { void alertDialog({ title: 'Could not update run', message: e.message, tone: 'danger' }) },
  })
  const deleteRun = useMutation({
    mutationFn: async (force: boolean) => {
      const reason = await promptDialog({
        title:       `Delete run ${run.run_number}?`,
        placeholder: 'Reason (kept on the audit trail)',
        allowEmpty:  true,
        confirm:     'Delete',
        tone:        'danger',
      })
      if (reason === null) return false   // cancelled
      const { error } = await withTimeout(supabase.rpc('delete_run', {
        p_run_id: run.run_id,
        p_reason: reason.trim() || null,
        p_force:  force,
      }), 'Runs: delete_run')
      if (error) {
        if (error.message.includes('pass force = true')) {
          const ok = await confirmDialog({
            title:   'Run has dependencies',
            message: `${error.message}\n\nForce-delete anyway? Pallets, deliveries, QC and labour shifts linked to this run will be orphaned.`,
            confirm: 'Force delete',
            tone:    'danger',
          })
          if (!ok) throw new ValidationError('cancelled')
          const { error: e2 } = await withTimeout(supabase.rpc('delete_run', {
            p_run_id: run.run_id,
            p_reason: reason.trim() || null,
            p_force:  true,
          }), 'Runs: delete_run')
          if (e2) throw e2
          return true
        }
        throw error
      }
      return true
    },
    onSuccess: (didDelete) => {
      if (!didDelete) return
      queryClient.invalidateQueries({ queryKey: ['runs'] })
      onBack()
    },
    onError: (e: Error) => {
      if (e.message === 'cancelled') return
      void alertDialog({ title: 'Delete failed', message: e.message, tone: 'danger' })
    },
  })

  // Delete asks its questions INSIDE the mutation: dismissing the reason
  // prompt resolves with `false`, and declining the force-delete throws the
  // `cancelled` sentinel. Neither is a deleted run, so neither may show a
  // tick or a red card — the same two tests deleteRun's own handlers make.
  const deleteRunFeedback = {
    isPending: deleteRun.isPending,
    isSuccess: deleteRun.isSuccess && deleteRun.data === true,
    isError:   deleteRun.isError && deleteRun.error?.message !== 'cancelled',
    error:     deleteRun.error,
    reset:     deleteRun.reset,
  }

  // Untip — reverses one tipping_log row via the undo_tipping RPC
  // (mig 174 + 517). Re-aggregates run + delivery + bin state. RPC
  // refuses on COMPLETE / ARCHIVED runs and surfaces the reason; we
  // mirror the role gate client-side so the button only shows for
  // users the RPC will accept.
  const undoTip = useMutation({
    mutationFn: async (tippingId: string) => {
      // Writes go through the isolated save client — the shared client's auth
      // lock can hold an RPC open forever on a busy page (repo standard).
      const db = saveDb ?? await ensureSaveDb()
      if (!db) throw new ValidationError('Session not ready — refresh and try again.')
      const { error } = await withTimeout(db.rpc('undo_tipping', { p_tip_id: tippingId }), 'Runs: undo_tipping')
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['run-tipping', run.run_id] })
      queryClient.invalidateQueries({ queryKey: ['runs'] })
      queryClient.invalidateQueries({ queryKey: ['run-linked-deliveries', run.run_id] })
      queryClient.invalidateQueries({ queryKey: ['ready-to-tip'] })
      // An undone BATCH tip returns the batch to the Released pool — refresh
      // the Tipping batch list + the Precool board so it reappears there.
      queryClient.invalidateQueries({ queryKey: ['ready-batches'] })
      queryClient.invalidateQueries({ queryKey: ['cooling-batches'] })
      // The lot is gone: the Lots view moves its packout to Unassigned, and an
      // undone stored bin is back on the Rebin stock sheet.
      queryClient.invalidateQueries({ queryKey: ['run-lots', run.run_id] })
      queryClient.invalidateQueries({ queryKey: ['rebin-stock'] })
    },
    onError: (e: Error) => {
      if (e.message === 'cancelled') return
      void alertDialog({ title: 'Could not undo tip', message: e.message, tone: 'danger' })
    },
  })

  // Pallets — start from pallet_dispatch_lines (mig 303) so the run picks
  // up its source slice on build-ups. We then hydrate the full pallet
  // record from `pallets` for coldroom_grid + parent_pallet_id; carton +
  // kg get OVERWRITTEN with the line-summed slice so totals reflect this
  // run's portion of any build-up rather than the full pallet weight.
  const palletsQ = useQuery({
    queryKey: ['run-pallets', run.run_id],
    queryFn: async () => {
      const linesRes = await supabase
        .from('pallet_dispatch_lines')
        .select('pallet_id, carton_count, net_mass_kg, run_id')
        .eq('source_run_id', run.run_id)
      if (linesRes.error) throw linesRes.error
      const sliceByPallet = new Map<string, { cartons: number; kg: number; primaryRunId: string }>()
      for (const l of (linesRes.data ?? []) as any[]) {
        const cur = sliceByPallet.get(l.pallet_id) ?? { cartons: 0, kg: 0, primaryRunId: l.run_id }
        cur.cartons += Number(l.carton_count ?? 0)
        cur.kg      += Number(l.net_mass_kg  ?? 0)
        sliceByPallet.set(l.pallet_id, cur)
      }
      const palletIds = Array.from(sliceByPallet.keys())
      if (palletIds.length === 0) return [] as Pallet[]
      const palletsRes = await supabase
        .from('pallets').select(
          'pallet_id,status,pallet_type,spec,grade,size_code,net_weight_kg,gross_weight_kg,carton_count,' +
          'target_market,destination,pack_type,mark,regime_code,pick_no,ssc_id,coldroom_grid,coldroom_overflow,loading_bay_lane,' +
          'intake_no,consignment_no,packed_at,born_date,parent_pallet_id,' +
          'ppecb_consignment_id,truck_consignment_id,loading_mission_id,pallet_lines(count)'
        )
        .in('pallet_id', palletIds).is('deleted_at', null)
        .order('packed_at', { ascending: false })
      if (palletsRes.error) throw palletsRes.error
      const palletRows = (palletsRes.data ?? []) as any[]

      // Resolve the linked consignment / mission *numbers* the same defensive
      // way the Pallets page does — dedup FK ids, then one batched lookup each
      // (a PostgREST embed on a load-bearing list query can silently zero the
      // parent response, so we keep these as follow-up fetches).
      const truckConsIds = Array.from(new Set(palletRows.map(p => p.truck_consignment_id).filter(Boolean))) as string[]
      const ppecbConsIds = Array.from(new Set(palletRows.map(p => p.ppecb_consignment_id).filter(Boolean))) as string[]
      const missionIds   = Array.from(new Set(palletRows.map(p => p.loading_mission_id).filter(Boolean)))   as string[]
      const [truckMap, ppecbMap, missionMap] = await Promise.all([
        truckConsIds.length === 0
          ? Promise.resolve(new Map<string, string>())
          : supabase.from('truck_consignments')
              .select('truck_consignment_id, consignment_number')
              .in('truck_consignment_id', truckConsIds)
              .then(r => new Map<string, string>((r.data ?? []).map((c: any) => [c.truck_consignment_id, c.consignment_number]))),
        ppecbConsIds.length === 0
          ? Promise.resolve(new Map<string, string>())
          : supabase.from('ppecb_consignments')
              .select('consignment_id, consignment_number')
              .in('consignment_id', ppecbConsIds)
              .then(r => new Map<string, string>((r.data ?? []).map((c: any) => [c.consignment_id, c.consignment_number]))),
        missionIds.length === 0
          ? Promise.resolve(new Map<string, { mission_number: string; dispatched_at: string | null }>())
          : supabase.from('loading_missions')
              .select('mission_id, mission_number, dispatched_at')
              .in('mission_id', missionIds)
              .then(r => new Map<string, { mission_number: string; dispatched_at: string | null }>(
                (r.data ?? []).map((m: any) => [m.mission_id, { mission_number: m.mission_number, dispatched_at: m.dispatched_at }]),
              )),
      ])

      return palletRows.map(p => {
        const slice = sliceByPallet.get(p.pallet_id)
        const embed = p.pallet_lines
        const count = Array.isArray(embed) ? (embed[0]?.count ?? 0) : (embed?.count ?? 0)
        const { pallet_lines: _drop, ...rest } = p
        const mission = p.loading_mission_id ? missionMap.get(p.loading_mission_id) : null
        return {
          ...rest,
          carton_count:  slice?.cartons ?? p.carton_count,
          net_weight_kg: slice?.kg      ?? p.net_weight_kg,
          run_id:        slice?.primaryRunId,
          is_buildup:    slice ? slice.primaryRunId !== run.run_id : false,
          line_count:               count,
          truck_consignment_number: p.truck_consignment_id ? (truckMap.get(p.truck_consignment_id) ?? null) : null,
          ppecb_consignment_number: p.ppecb_consignment_id ? (ppecbMap.get(p.ppecb_consignment_id) ?? null) : null,
          mission_number:           mission?.mission_number ?? null,
          mission_dispatched_at:    mission?.dispatched_at ?? null,
        } as Pallet
      })
    },
  })

  // Aggregated client refs from the deliveries tipped into this run.
  // View defined in mig 144 — joins tipping_log → deliveries.
  const { data: clientRefs = null } = useQuery<string | null>({
    queryKey: ['run-client-refs', run.run_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('runs_client_refs')
        .select('client_refs')
        .eq('run_id', run.run_id)
        .maybeSingle()
      if (error) throw error
      return (data?.client_refs ?? null) as string | null
    },
  })

  // Tipping log. Embed the source delivery's client_ref + DN number so
  // each tip card can show which delivery it came from. PostgREST
  // returns the embed as an object (or array — we coerce below).
  const { data: tips = [] } = useQuery({
    queryKey: ['run-tipping', run.run_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tipping_log')
        .select('tipping_id,bin_quantity,gross_weight_kg,net_weight_kg,tipped_at,batch_id,delivery_id,deliveries(client_ref_no,delivery_note_number),cooling_batches(batch_number),bins(bin_number,source_pallet_id,source_carton_count)')
        .eq('run_id', run.run_id)
        .order('tipped_at', { ascending: true })
      if (error) throw error
      return (data ?? []).map((r: any) => {
        const d = Array.isArray(r.deliveries) ? r.deliveries[0] : r.deliveries
        const b = Array.isArray(r.cooling_batches) ? r.cooling_batches[0] : r.cooling_batches
        return {
          tipping_id:           r.tipping_id,
          bin_quantity:         r.bin_quantity,
          gross_weight_kg:      r.gross_weight_kg,
          net_weight_kg:        r.net_weight_kg,
          tipped_at:            r.tipped_at,
          client_ref_no:        d?.client_ref_no        ?? null,
          delivery_note_number: d?.delivery_note_number ?? null,
          delivery_id:          r.delivery_id ?? null,
          batch_id:             r.batch_id              ?? null,
          batch_number:         b?.batch_number         ?? null,
          ...binSourceOf(r),
        } as TipEntry
      })
    },
  })

  const { data: pallets = [] } = palletsQ

  // Rejects
  const rejectsQ = useQuery({
    queryKey: ['run-rejects', run.run_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reject_log').select('reject_id,reject_source,kind,weight_kg,bin_count,defect_reasons,logged_at')
        .eq('run_id', run.run_id)
        // Mig 355 soft-deletes reject rows and keeps them for audit; the
        // operator-facing list on Rejects.tsx filters them, this tab did not,
        // so a corrected mis-log stayed in the run's reject and waste totals
        // (and in the per-source breakdown below) forever.
        .is('deleted_at', null)
        .order('logged_at', { ascending: false })
      if (error) throw error
      return data as RejectEntry[]
    },
  })
  const { data: rejects = [] } = rejectsQ

  // Token allocations (FR6)
  const { data: tokens = [] } = useQuery({
    queryKey: ['run-tokens', run.run_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('packing_token_allocations')
        .select('*')
        .eq('run_id', run.run_id)
        .order('allocated_at', { ascending: false })
      if (error) throw error
      return data as TokenAllocation[]
    },
  })

  // QC summary + FR14 photo evidence counts for the Verify gate.
  const qcQ = useQuery({
    queryKey: ['run-qc', run.run_id],
    queryFn: async (): Promise<QCSummary & {
      packer_without_photo:       number
      titration_warning_no_photo: number
    }> => {
      // Counts come from get_qc_run_verification_metrics (mig 20260704120000) —
      // across BOTH the dynamic engine (SYS_* runs) + the legacy tables, so citrus
      // (which logs QC via the dynamic engine) is counted. avgBrix/avgAcid stay on
      // legacy titration for display (dynamic brix/acid unify in a later PR).
      const [mRes, titRes] = await Promise.all([
        supabase.rpc('get_qc_run_verification_metrics', { p_run_id: run.run_id }),
        supabase.from('qc_titration').select('brix,acid').eq('run_id', run.run_id).is('deleted_at', null),
      ])
      if (mRes.error) throw mRes.error
      const m = mRes.data as QcVerificationMetrics
      const tits = (titRes.data ?? []) as { brix: number | null; acid: number | null }[]
      const brixVals = tits.filter(t => t.brix).map(t => t.brix!)
      const acidVals = tits.filter(t => t.acid).map(t => t.acid!)
      return {
        full_box:  m.full_box_count,
        packer:    m.packer_problem_count,
        titration: m.titration_count,
        warnings:  m.titration_warning_count,
        avgBrix:   brixVals.length ? brixVals.reduce((a, b) => a + b, 0) / brixVals.length : null,
        avgAcid:   acidVals.length ? acidVals.reduce((a, b) => a + b, 0) / acidVals.length : null,
        packer_without_photo:       m.packer_without_photo,
        titration_warning_no_photo: m.titration_warning_no_photo,
      }
    },
  })
  const { data: qc } = qcQ

  // Dynamic-engine QC summary for this run (mig 176/178). Pulled
  // separately so it doesn't disturb the legacy `qc` shape which feeds
  // the Verify gate. Aggregates qc_runs + their MULTI_SELECT responses
  // into per-template counts + a defect tally.
  const dynamicQcQ = useQuery({
    queryKey: ['run-dynamic-qc', run.run_id],
    // Same function flagged on origin/main with higher numbers (cc 22, cog 29); S3 only made it smaller.
    // fallow-ignore-next-line complexity
    queryFn: async () => {
      const { data: runRows, error: runsErr } = await supabase.from('qc_runs')
        .select('qc_run_id, template_code, template_name, result, status')
        .eq('source_run_id', run.run_id)
        .is('deleted_at', null)
      if (runsErr) throw runsErr
      const ids = (runRows ?? []).map(r => r.qc_run_id as string)
      const defectTally = await fetchRunDefectTally(ids)
      const byTemplate = new Map<string, { template_code: string; template_name: string; total: number; pass: number; fail: number; conditional: number; in_progress: number }>()
      for (const r of (runRows ?? []) as { qc_run_id: string; template_code: string; template_name: string; result: 'PASS'|'FAIL'|'CONDITIONAL'|null; status: 'IN_PROGRESS'|'COMPLETED'|'CANCELLED' }[]) {
        if (!byTemplate.has(r.template_code)) {
          byTemplate.set(r.template_code, { template_code: r.template_code, template_name: r.template_name, total: 0, pass: 0, fail: 0, conditional: 0, in_progress: 0 })
        }
        const e = byTemplate.get(r.template_code)!
        e.total += 1
        if (r.status === 'IN_PROGRESS')   e.in_progress += 1
        if (r.result === 'PASS')          e.pass += 1
        if (r.result === 'FAIL')          e.fail += 1
        if (r.result === 'CONDITIONAL')   e.conditional += 1
      }
      return {
        templates: Array.from(byTemplate.values()).sort((a, b) => b.total - a.total),
        defects:   defectTally,
        total:     runRows?.length ?? 0,
        completed: (runRows ?? []).filter(r => r.status === 'COMPLETED').length,
        fail:      (runRows ?? []).filter(r => r.result === 'FAIL').length,
        conditional: (runRows ?? []).filter(r => r.result === 'CONDITIONAL').length,
      }
    },
    refetchInterval: POLL.standard,
  })
  const { data: dynamicQc } = dynamicQcQ

  // The QC tab draws from BOTH queries — gate its empty branch on their
  // combined state so a failed/paused load never reads as "No QC checks".
  const qcTabQ = {
    isPending:  qcQ.isPending  || dynamicQcQ.isPending,
    isFetching: qcQ.isFetching || dynamicQcQ.isFetching,
    isPaused:   qcQ.isPaused   || dynamicQcQ.isPaused,
    isError:    qcQ.isError    || dynamicQcQ.isError,
    error:      qcQ.error      ?? dynamicQcQ.error,
    refetch:    () => Promise.all([qcQ.refetch(), dynamicQcQ.refetch()]),
  }

  const totalRejectKg = rejects.filter(r => r.kind !== 'WASTE').reduce((s, r) => s + r.weight_kg, 0)
  const totalWasteKg  = rejects.filter(r => r.kind === 'WASTE').reduce((s, r) => s + r.weight_kg, 0)

  const TABS: ModuleTab<DetailTab>[] = [
    { key: 'overview', label: 'Overview', icon: LayoutGrid },
    { key: 'tipping',  label: 'Tipping',  icon: Truck,        count: tips.length },
    { key: 'pallets',  label: 'Pallets',  icon: Package,      count: pallets.length },
    { key: 'rejects',  label: 'Rejects',  icon: Trash2,       count: rejects.length },
    { key: 'qc',       label: 'QC',       icon: FlaskConical, count: (qc?.full_box ?? 0) + (qc?.packer ?? 0) + (qc?.titration ?? 0) },
  ]

  return (
    <div className="min-h-full bg-canvas flex flex-col">
      <MutationOverlays of={[
        [editStartedAt,     'Run date saved'],
        [deleteRunFeedback, 'Run deleted'],
        [undoTip,           'Tip undone'],
        [lifecycle.stopTipping,      'Tipping stopped'],
        [lifecycle.reopenForTipping, 'Run reopened for tipping'],
        [lifecycle.completeRun,      'Run completed'],
      ]} />
      {/* Header — PW-13: the status is the title's badge, so on a phone it
          leads the grower line instead of squeezing the run number into
          "RUN-2026-/0412" (packhouse audit PM-PRD-16). */}
      <PageHeader onBack={onBack} title={run.run_number}
        subtitle={<>{run.grower} · {run.variety}</>}
        badge={<span className="inline-flex items-center gap-1.5"><StatusPill object="run" status={run.status} label={runStatusLabel(run.status)} />{run.verified_at && <StatusPill object="run" status="VERIFIED" />}</span>}
        actions={<>
          {/* Standalone trace page — shrinkage/lifecycle/cooling-batch record in a
              print-friendly report. Was previously unreachable from anywhere in
              the app (E2E finding 2026-06-11). */}
          <Link to={`/runs/${run.run_id}/print`}
            title="Open the full run report (lifecycle, shrinkage, cooling batches)"
            className="btn-ghost btn-sm shrink-0 flex items-center gap-1">
            <FileText size={14} /> Full report
          </Link>
          <RunQcLink runId={run.run_id} />
          {offersChangeFruit && (
            <Button variant="secondary" size="sm" onClick={() => setChangingFruit(true)}>Change fruit</Button>
          )}
          {canDelete && (
            <button
              onClick={() => deleteRun.mutate(false)}
              disabled={deleteRun.isPending}
              title="Delete run (super admin; a run with pallets that stood in a coldroom needs a manager or admin)"
              className="min-w-[44px] min-h-[44px] px-2.5 inline-flex items-center justify-center gap-1.5 rounded-xl text-sm text-red-400 hover:text-red-300 hover:bg-red-900/20">
              <Trash2 size={16} /> Delete run
            </button>
          )}
        </>} />

      {growerOpen && (
        <Modal title="What the grower sees" subtitle={run.grower} onClose={() => setGrowerOpen(false)} noFooter size="xl">
          <GrowerPreview run={run} />
        </Modal>
      )}

      {changingFruit && <ChangeRunFruitModal run={run} onClose={() => setChangingFruit(false)} />}

      {run.status === 'ACTIVE' && !isExternalReceiptRun(run.run_kind) && (
        <TipIntoRunLink runId={run.run_id} status={run.status} inRow />
      )}

      {/* UF-10: the register cards' lifecycle buttons, on the run's own page.
          Secondary tone, so Build pallets stays the one primary. Same gate
          and state machine as the cards (runActions); the server decides. */}
      {isManagerOrAbove(me.data?.role) && !isExternalReceiptRun(run.run_kind) && (
        <div className="px-4 pt-3">
          <RunActionButtons
            status={run.status as RunStatus}
            runNumber={run.run_number}
            isAdmin={meIsAdmin}
            isManager
            canArchive={canArchiveRun(run)}
            busy={lifecycle.busy}
            onAction={kind => lifecycle.act(kind, run)}
          />
          <RunOpenItems run={run} onShowWaiting={() => {
            setTab('overview')
            window.setTimeout(() => document.getElementById('run-fed')?.scrollIntoView?.({ block: 'center' }), 50)
          }} />
        </div>
      )}

      {/* UF-05: the Done panel on the run page. Verify & publish switches to
          Overview and brings the Verify card into view. */}
      {done && done.run_id === run.run_id && onDismissDone && (
        <RunCompleteDone done={done} onDismiss={onDismissDone}
          onVerify={() => {
            setTab('overview')
            onDismissDone()
            setTimeout(() => document.getElementById('run-verify-card')?.scrollIntoView({ block: 'start' }), 50)
          }} />
      )}

      {/* Tab bar */}
      <div className="border-b border-line-subtle">
        <ModuleTabBar tabs={TABS} value={tab} onChange={setTab} />
      </div>

      <div className="flex-1 overflow-y-auto">

        {/* ── OVERVIEW TAB ── */}
        {tab === 'overview' && (
          <div className="p-4 space-y-4">
            {/* UF-04: how fed the run is, above the tiles */}
            <RunFedLine runId={run.run_id} status={run.status} />
            {/* UF-01 (U21): packaging used, read from pallets and run text */}
            <RunPackagingCard runId={run.run_id} runNumber={run.run_number} />
            {/* Key stats */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Tipped',    val: `${run.tipped_net_weight_kg.toFixed(0)} kg`,  colour: 'text-fg' },
                { label: 'Packed',    val: `${run.packed_weight_kg.toFixed(0)} kg`,      colour: 'text-fg' },
                { label: 'Rejects',   val: `${run.reject_weight_kg.toFixed(0)} kg`,      colour: run.reject_weight_kg > 0 ? 'text-red-400' : 'text-fg' },
                { label: 'Waste',     val: `${run.waste_weight_kg.toFixed(0)} kg`,       colour: 'text-fg-muted' },
                { label: 'Packout · of tipped', val: fmtPct(packoutPair(run).ofTipped),
                  colour: !packoutPair(run).settled ? 'text-fg' : packoutPair(run).lowOfTipped ? 'text-amber-400' : 'text-green-400' },
                { label: 'Packout · grower',
                  val: packoutPair(run).capture === 'NOT_CAPTURED' ? 'rejects not captured' : fmtPct(packoutPair(run).grower),
                  colour: packoutPair(run).capture === 'NOT_CAPTURED' ? 'text-amber-300 text-sm' : 'text-green-400' },
                { label: 'Pallets',   val: `${run.pallet_count}`,                         colour: 'text-fg' },
              ].map(s => (
                <div key={s.label} className="card py-3 text-center">
                  <p className="text-caption text-fg-muted">{s.label}</p>
                  <p className={clsx('font-bold text-lg tabular-nums', s.colour)}>{s.val}</p>
                </div>
              ))}
            </div>

            {/* FR25 Verify & Publish — UF-03: second block, right after the
                tiles; a failed QC read shows Retry, never a vanished card. */}
            <div id="run-verify-card">
              <VerifyQcGate status={run.status} qcQ={qcQ}>
                {q => <VerifyCard run={run} qc={q} />}
              </VerifyQcGate>
            </div>

            {/* Balance packout — admin-only weight nudge (mig 902). Corrects a
                run's packout % when the recorded bin/pallet weights are
                systematically off (mis-set tare, scale drift). */}
            {meIsAdmin && (
              <button
                onClick={() => setBalancing(true)}
                className="w-full text-caption font-semibold px-3 py-2 rounded-xl border border-line-subtle text-fg-secondary hover:border-emerald-700 hover:text-emerald-300 flex items-center justify-center gap-1.5">
                <Scale size={14} /> Balance packout…
              </button>
            )}

            {/* Run info */}
            <div className="card space-y-2 text-sm">
              {[
                { label: 'Grower',         val: run.grower },
                { label: 'Client ref(s)',  val: clientRefs ?? '—' },
                { label: 'Commodity',      val: run.commodity ?? '—' },
                { label: 'Variety',        val: run.variety },
                { label: 'PUC',            val: run.puc ?? '—' },
                { label: 'Packhouse code', val: run.packhouse_code ?? '—' },
                { label: 'Completed',      val: run.completed_at ? new Date(run.completed_at).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }) : '—' },
                { label: 'Verified',       val: run.verified_at  ? new Date(run.verified_at).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }) : 'Not verified' },
              ].map(row => (
                <div key={row.label} className="flex justify-between">
                  <span className="text-fg-muted">{row.label}</span>
                  {row.label === 'Grower'
                    ? <RunGrowerLink growerId={run.grower_id} name={run.grower} />
                    : <span className="text-fg font-medium">{row.val}</span>}
                </div>
              ))}
              {/* Started — inline-editable for admins */}
              <div className="flex justify-between items-center">
                <span className="text-fg-muted">Started</span>
                {editingStarted ? (
                  <div className="flex items-center gap-1.5">
                    <input type="date"
                      className="bg-surface text-fg text-caption rounded-lg px-1.5 py-0.5 border border-line"
                      max={sastTodayYMD()}
                      value={editStartedDate}
                      onChange={e => setEditStartedDate(e.target.value)} />
                    <button onClick={() => editStartedAt.mutate()}
                      disabled={editStartedAt.isPending}
                      className="text-emerald-400 hover:text-emerald-300 text-caption">Save</button>
                    <button onClick={() => { setEditingStarted(false); setEditStartedDate(run.started_at?.slice(0, 10) ?? '') }}
                      className="text-fg-muted hover:text-fg-secondary text-caption">Cancel</button>
                  </div>
                ) : (
                  <span className="text-fg font-medium flex items-center gap-2">
                    {new Date(run.started_at).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' })}
                    {meIsAdmin && (
                      <button onClick={() => setEditingStarted(true)}
                        className="text-link hover:text-blue-300 text-caption"
                        title="Back-date this run (admin)">
                        ✎
                      </button>
                    )}
                  </span>
                )}
              </div>
            </div>

            {/* What was ACTUALLY tipped, when that differs from the run's
                declared variety. Renders nothing on a clean run. */}
            <RunVarietyPanel runId={run.run_id} declared={run.variety} />

            {/* Linked runs (T2): in and out by week, day and variety. Renders
                nothing on a one-day, one-variety run. */}
            <RunFlowGroups run={run} />

            {/* Linked runs (T9): fed by → this run → fed into. Nothing on a run
                that links to no other run. */}
            <RunFlowLinks run={run} />

            {run.notes && (
              <div className="card">
                <p className="text-caption text-fg-muted mb-1">Notes</p>
                <p className="text-sm text-fg-secondary">{run.notes}</p>
              </div>
            )}

            {/* Shrinkage card — shown once tipping has happened */}
            {run.tipped_net_weight_kg > 0 && (
              <ShrinkageCard run={run} />
            )}

            {/* QC summary — combines legacy log counts + the dynamic
                engine's pass/fail breakdown. Tap a template row to jump
                into the QC tab. */}
            <RunQcSummaryCard
              legacy={qc}
              dynamic={dynamicQc}
              onOpenQcTab={() => setTab('qc')} />


            {/* CSV export — available any time for a manager's records */}
            <RunExportCard run={run} />

            {/* UF-13: the grower preview, gated on the run itself (verified
                with a share token), not on the Verify card, which mounts only
                when the QC read worked. */}
            {run.verified_at && run.grower_share_token && (
              <button type="button" onClick={() => setGrowerOpen(true)}
                className="w-full min-h-[44px] text-sm font-semibold px-3 py-2 rounded-xl border border-line-subtle text-fg-secondary hover:text-fg flex items-center justify-center gap-1.5">
                <ShieldCheck size={16} /> See what the grower sees
              </button>
            )}

            {/* UF-13: sticker ranges, folded. Rare job, one tap away. */}
            <details className="card">
              <summary className="min-h-[44px] flex items-center cursor-pointer text-sm font-semibold text-fg">
                Sticker ranges{tokens.length > 0 ? ` (${tokens.length})` : ''}
              </summary>
              <div className="-mx-4 -mb-4"><TokensTab run={run} allocations={tokens} palletCount={pallets.length} /></div>
            </details>
          </div>
        )}

        {/* ── PALLETS TAB ── */}
        {tab === 'pallets' && (
          <div className="p-4 space-y-2">
            {pallets.length === 0 && (
              <QueryStateGate query={palletsQ} noun="pallets"
                empty={
                  <div className="flex flex-col items-center justify-center py-16 text-fg-muted">
                    <Package size={48} className="mb-3 opacity-40" />
                    <p>No pallets</p>
                  </div>
                } />
            )}
            {pallets.length > 0 && (
              <div className="flex items-start justify-between gap-2">
                {canMovePallets && pallets.some(isSelectablePallet) ? (
                  <p className="text-caption text-fg-muted px-0.5 flex-1">
                    On the wrong run or wrong variety? Tick the pallets and move them to the correct run.
                    Dispatched / consigned pallets need an override confirm.
                  </p>
                ) : <span className="flex-1" />}
                <PalletViewToggle value={palletView} onChange={setPalletView} />
              </div>
            )}

            {pallets.length > 0 && palletView === 'table' && (
              <PalletTable
                pallets={pallets}
                selectable={canMovePallets}
                isRowSelectable={isSelectablePallet}
                selectedIds={selectedToMove}
                onToggleSelect={toggleMoveSelect}
              />
            )}

            {pallets.length > 0 && palletView === 'cards' && pallets.map(p => {
              const movable  = canMovePallets && isSelectablePallet(p)
              const checked  = selectedToMove.has(p.pallet_id)
              return (
                <div key={p.pallet_id}
                  className={clsx('card space-y-2', checked && 'ring-1 ring-blue-500')}>
                  <div className="flex items-start gap-2">
                    {movable && (
                      <input type="checkbox" checked={checked}
                        onChange={() => toggleMoveSelect(p.pallet_id)}
                        title="Select to move to another run"
                        className="mt-1 h-4 w-4 shrink-0 accent-blue-500 cursor-pointer" />
                    )}
                    <div className="flex items-start justify-between gap-2 flex-1 min-w-0">
                      <div className="min-w-0 space-y-0.5">
                        <Link
                          to={`/pallets/${p.pallet_id}`}
                          className="font-mono text-sm font-bold text-fg hover:text-blue-300 hover:underline">
                          {p.pallet_id}
                        </Link>
                        <PalletSummaryInfo pallet={p} variety={run.variety} />
                        {(p.parent_pallet_id || p.is_buildup) && (
                          <div className="flex gap-2 flex-wrap">
                            {p.parent_pallet_id && <span className="text-caption text-orange-400">Repack</span>}
                            {p.is_buildup && <span className="text-caption text-amber-400">build-up · this run's slice</span>}
                          </div>
                        )}
                      </div>
                      <StatusPill object="pallet" status={p.status} className="shrink-0" />
                    </div>
                  </div>
                </div>
              )
            })}

          </div>
        )}

        {/* ── TIPPING TAB ── */}
        {tab === 'tipping' && (
          <div className="p-4 space-y-3">
            {/* Add another delivery — ACTIVE runs only (F02 UF-04): a stopped run
                is on no board, so linking to it would feed nothing. */}
            {run.status === 'ACTIVE' && (
              <AddDeliveryToRunButton run={run} />
            )}
            {/* UF-11: the linked deliveries still to tip, each a link */}
            <RunFedLine runId={run.run_id} status={run.status} />
            {/* Summary */}
            {tips.length > 0 && (
              <div className="card grid grid-cols-3 gap-2 text-center py-3">
                <div>
                  <p className="text-caption text-fg-muted">Total Bins</p>
                  <p className="font-bold tabular-nums text-fg">{tips.reduce((s, t) => s + t.bin_quantity, 0)}</p>
                </div>
                <div>
                  <p className="text-caption text-fg-muted">Gross</p>
                  <p className="font-bold tabular-nums text-fg">{tips.reduce((s, t) => s + t.gross_weight_kg, 0).toFixed(0)} kg</p>
                </div>
                <div>
                  <p className="text-caption text-fg-muted">Net</p>
                  <p className="font-bold tabular-nums text-success">{tips.reduce((s, t) => s + t.net_weight_kg, 0).toFixed(0)} kg</p>
                </div>
              </div>
            )}

            {tips.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-fg-muted">
                <Package size={48} className="mb-3 opacity-40" />
                <p>No tipping events</p>
              </div>
            )}

            <RunLotsSection runId={run.run_id} runNumber={run.run_number} status={run.status} />

            {tips.map((t, i) => {
              // Undo is only meaningful while the run can still receive
              // changes — the RPC blocks COMPLETE / ARCHIVED. Hide the
              // button rather than letting the operator hit a 42501.
              const canUndoThis =
                canUndoTip &&
                (run.status === 'ACTIVE' || run.status === 'TIPPING_COMPLETE')
              return (
                <div key={t.tipping_id} className="card">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-caption text-fg-muted mb-0.5">Tip {i + 1}</p>
                      <p className="font-bold tabular-nums text-fg">
                        {t.bin_quantity} {t.batch_id ? 'crate' : 'bin'}{t.bin_quantity !== 1 ? 's' : ''}
                      </p>
                      <p className="text-caption text-fg-muted mt-0.5">
                        {t.gross_weight_kg.toFixed(1)} kg gross · <span className="text-success">{t.net_weight_kg.toFixed(1)} kg net</span>
                      </p>
                      {t.batch_number && (
                        <p className="text-caption text-fg-muted mt-1">
                          Batch <span className="text-blue-300 font-mono">{t.batch_number}</span>
                        </p>
                      )}
                      <BinTipSource facts={t} />
                      {(t.client_ref_no || t.delivery_note_number) && (
                        <p className="text-caption text-fg-muted mt-1">
                          {t.client_ref_no && <>Ref <span className="text-fg">{t.client_ref_no}</span></>}
                          {t.client_ref_no && t.delivery_note_number && <span className="text-fg-muted"> · </span>}
                          {t.delivery_note_number && <>DN <RunDeliveryLink deliveryId={t.delivery_id}>{t.delivery_note_number}</RunDeliveryLink></>}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <p className="text-caption text-fg-muted">
                        {new Date(t.tipped_at).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })}
                      </p>
                      {canUndoThis && (
                        <button
                          onClick={async () => {
                            const ok = await confirmUndoTip({
                              tippingId: t.tipping_id,
                              title:   `Undo Tip ${i + 1}?`,
                              message: undoTipMessage({
                                kind: t.batch_id ? 'batch' : t.bin_number ? 'bin' : 'delivery',
                                label: t.batch_id ? `Batch ${t.batch_number ?? ''}` : (t.bin_number ?? ''),
                                bin_quantity: t.bin_quantity,
                                net_weight_kg: t.net_weight_kg,
                              }),
                            })
                            if (!ok) return
                            undoTip.mutate(t.tipping_id)
                          }}
                          disabled={undoTip.isPending}
                          className="text-caption text-red-400 hover:text-red-300 disabled:opacity-50 flex items-center gap-1">
                          <X size={11} /> Undo tip
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* ── REJECTS TAB ── */}
        {tab === 'rejects' && (
          <div className="p-4 space-y-3">
            {canOpenRejects === true && (run.status === 'ACTIVE' || run.status === 'TIPPING_COMPLETE') && (
              <Link to={`/rejects?run=${run.run_id}`}
                className="btn-secondary w-full justify-center min-h-[44px] inline-flex items-center">
                Log rejects for this run
              </Link>
            )}
            {/* Per-source breakdown */}
            {rejects.length > 0 && (
              <div className="card space-y-3">
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div>
                    <p className="text-caption text-fg-muted">Rejects</p>
                    <p className={clsx('font-bold tabular-nums', totalRejectKg > 0 ? 'text-red-400' : 'text-fg')}>{totalRejectKg.toFixed(1)} kg</p>
                  </div>
                  <div>
                    <p className="text-caption text-fg-muted">Waste</p>
                    <p className="font-bold tabular-nums text-fg-muted">{totalWasteKg.toFixed(1)} kg</p>
                  </div>
                  <div>
                    <p className="text-caption text-fg-muted">Tipped Net</p>
                    <p className="font-bold tabular-nums text-fg">{run.tipped_net_weight_kg.toFixed(0)} kg</p>
                  </div>
                  <div>
                    <p className="text-caption text-fg-muted">Reject %</p>
                    <p className="font-bold tabular-nums text-amber-400">
                      {run.tipped_net_weight_kg > 0
                        ? (totalRejectKg / run.tipped_net_weight_kg * 100).toFixed(1)
                        : '—'}%
                    </p>
                  </div>
                </div>

                {run.tipped_net_weight_kg > 0 && (
                  <div className="border-t border-line pt-3 space-y-2">
                    {rejectBreakdownSources(rejects.map(r => r.reject_source), isBerry).map(src => {
                      // kind !== 'WASTE' matches the totalRejectKg headline this
                      // breakdown sits under and divides by. reject_source is
                      // orthogonal to kind, so without it a WASTE row logged
                      // against e.g. PRESORTER was added to the PRESORTER line —
                      // re-lumping the two kinds that mig 307 deliberately split
                      // into runs.reject_weight_kg vs waste_weight_kg, and
                      // producing per-source bars that overrun their own total.
                      const kg = rejects.filter(r => r.reject_source === src.value && r.kind !== 'WASTE').reduce((s, r) => s + r.weight_kg, 0)
                      if (kg === 0) return null
                      const pct = (kg / run.tipped_net_weight_kg * 100).toFixed(1)
                      const barW = totalRejectKg > 0 ? Math.min(100, kg / totalRejectKg * 100) : 0
                      return (
                        <div key={src.value}>
                          <div className="flex justify-between text-caption mb-1">
                            <span className="text-fg-secondary font-medium">{src.label}</span>
                            <span className="text-fg-muted">{kg.toFixed(1)} kg · <span className="text-fg font-semibold">{pct}%</span> of tipped</span>
                          </div>
                          <div className="h-1.5 bg-raised rounded-full overflow-hidden">
                            <div className={clsx('h-full rounded-full', src.colour)} style={{ width: `${barW}%` }} />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {rejects.length === 0 && (
              <QueryStateGate query={rejectsQ} noun="rejects"
                empty={
                  <div className="flex flex-col items-center justify-center py-16 text-fg-muted">
                    <AlertTriangle size={48} className="mb-3 opacity-40" />
                    <p>No rejects logged</p>
                  </div>
                } />
            )}

            {rejects.map(r => {
              const src = sourceChip(r.reject_source)
              return (
                <div key={r.reject_id} className="card space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold tabular-nums text-red-400">{r.weight_kg.toFixed(1)} kg</p>
                      {src && (
                        <span className={clsx('text-caption text-on-fill rounded-full px-2 py-0.5 font-semibold', src.colour)}>
                          {src.label}
                        </span>
                      )}
                      {r.bin_count != null && (
                        <span className="text-caption text-fg-muted">{r.bin_count} bin{r.bin_count !== 1 ? 's' : ''}</span>
                      )}
                    </div>
                    <p className="text-caption text-fg-muted shrink-0">
                      {new Date(r.logged_at).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  {r.defect_reasons && r.defect_reasons.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {r.defect_reasons.map(d => (
                        <span key={d} className="text-caption bg-red-900/40 text-red-300 rounded-full px-2 py-0.5">{d}</span>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* ── QC TAB ── */}
        {tab === 'qc' && (
          <div className="p-4 space-y-4">
            {/* Count summary */}
            <div className={clsx('grid gap-3', hasTitrationQc ? 'grid-cols-3' : 'grid-cols-2')}>
              {[
                { label: 'Full Box',  val: qc?.full_box  ?? 0, colour: 'text-green-400' },
                { label: 'Packer',    val: qc?.packer    ?? 0, colour: 'text-orange-400' },
                ...(hasTitrationQc ? [{ label: 'Titration', val: qc?.titration ?? 0, colour: 'text-purple-400' }] : []),
              ].map(s => (
                <div key={s.label} className="card py-3 text-center">
                  <p className="text-caption text-fg-muted">{s.label}</p>
                  <p className={clsx('font-bold text-2xl', s.colour)}>{s.val}</p>
                </div>
              ))}
            </div>

            {/* Titration averages */}
            {(qc?.avgBrix != null || qc?.avgAcid != null) && (
              <div className="card space-y-2">
                <p className="text-caption text-fg-muted font-semibold uppercase tracking-wide">Titration Averages</p>
                <div className="grid grid-cols-2 gap-3">
                  {qc?.avgBrix != null && (
                    <div className="bg-raised/50 rounded-xl py-2 text-center">
                      <p className="text-caption text-fg-muted">Avg Brix</p>
                      <p className="font-bold tabular-nums text-success">{qc.avgBrix.toFixed(1)}</p>
                    </div>
                  )}
                  {qc?.avgAcid != null && (
                    <div className="bg-raised/50 rounded-xl py-2 text-center">
                      <p className="text-caption text-fg-muted">Avg Acid</p>
                      <p className={clsx('font-bold tabular-nums', qc.avgAcid <= 0.6 ? 'text-amber-400' : 'text-fg')}>
                        {qc.avgAcid.toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Acid warnings */}
            {(qc?.warnings ?? 0) > 0 && (
              <div className="rounded-xl bg-warning-soft border border-warning-edge px-4 py-3 flex items-center gap-2">
                <AlertTriangle size={16} className="text-warning shrink-0" />
                <p className="text-sm text-warning">
                  {qc!.warnings} titration{qc!.warnings > 1 ? 's' : ''} with low acid warning
                </p>
              </div>
            )}

            {/* Dynamic engine checks — per-template results + defect tally for this run */}
            {dynamicQc && dynamicQc.total > 0 && (
              <RunDynamicQcCard dynamic={dynamicQc} />
            )}

            {(!qc || (qc.full_box === 0 && qc.packer === 0 && qc.titration === 0))
              && (!dynamicQc || dynamicQc.total === 0) && (
              <QueryStateGate query={qcTabQ} noun="QC checks"
                empty={
                  <div className="flex flex-col items-center justify-center py-16 text-fg-muted">
                    <FlaskConical size={48} className="mb-3 opacity-40" />
                    <p>No QC checks logged</p>
                  </div>
                } />
            )}
          </div>
        )}


      </div>

      {/* Bulk-move action bar — pinned while any pallet is selected. OUTSIDE the tab body on
          purpose, as Reject Bins does: that body is `flex-1 overflow-y-auto` under a min-h-full
          root, so it never scrolls — the page does — yet it is still the box a sticky child pins
          to. Inside it the bar sat at the end of the list, ~6,000px down on a phone
          (packhouse audit PM-PRD-10, PW-04). */}
      {tab === 'pallets' && canMovePallets && selectedToMove.size > 0 && (
        <div className="sticky bottom-0 z-20 above-bottom-nav px-4 pt-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] bg-canvas/95 backdrop-blur border-t border-line-subtle flex items-center gap-2">
          <span className="text-sm text-fg font-semibold flex-1">
            {selectedToMove.size} selected
          </span>
          <button onClick={() => setSelectedToMove(new Set())}
            className="text-caption px-3 py-2 min-h-[44px] rounded-lg text-fg-muted hover:bg-surface">
            Clear
          </button>
          <button onClick={() => setBulkMoving(true)}
            className="text-caption font-semibold px-4 py-2 min-h-[44px] rounded-lg bg-blue-600 text-on-fill hover:bg-blue-500 flex items-center gap-1.5">
            <ArrowLeftRight size={14} /> Move to run…
          </button>
        </div>
      )}

      {bulkMoving && canMovePallets && selectedToMove.size > 0 && (
        <MovePalletDialog
          mode={{ kind: 'bulk', palletIds: [...selectedToMove] }}
          currentRunId={run.run_id}
          commodity={run.commodity}
          currentVariety={run.variety}
          requiresForce={pallets.some(p => selectedToMove.has(p.pallet_id) && palletNeedsForce(p))}
          onClose={() => setBulkMoving(false)}
          onMoved={() => setSelectedToMove(new Set())} />
      )}

      {balancing && meIsAdmin && (
        <BalancePackoutDialog
          run={run}
          bins={tips}
          pallets={pallets}
          onClose={() => setBalancing(false)} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tokens tab — FR6: allocate sticker ranges to this run.
// ---------------------------------------------------------------------------
function TokensTab({
  run, allocations, palletCount,
}: {
  run: Run
  allocations: TokenAllocation[]
  palletCount: number
}) {
  const qc = useQueryClient()
  const [prefix,   setPrefix]   = useState('')
  const [start,    setStart]    = useState('')
  const [count,    setCount]    = useState('')
  const [orderRef, setOrderRef] = useState('')
  const [notes,    setNotes]    = useState('')
  const [error,    setError]    = useState<string | null>(null)

  const totalAllocated = allocations.reduce((s, a) => s + (a.range_end - a.range_start + 1), 0)
  const startNum = parseInt(start) || 0
  const cnt      = parseInt(count) || 0
  const endNum   = cnt > 0 ? startNum + cnt - 1 : 0

  const allocate = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession(); const user = session?.user
      if (!user) throw new ValidationError('Not authenticated')
      if (startNum <= 0)   throw new ValidationError('Enter a starting token number')
      if (cnt    <= 0)     throw new ValidationError('Enter how many tokens to allocate')

      const { error: insertErr } = await withTimeout(supabase.from('packing_token_allocations').insert({
        run_id:          run.run_id,
        token_prefix:    prefix.trim(),
        range_start:     startNum,
        range_end:       endNum,
        order_reference: orderRef.trim() || null,
        notes:           notes.trim()    || null,
        allocated_by:    user.id,
      }), 'Runs: packing_token_allocations insert')
      if (insertErr) {
        // The exclusion constraint surfaces as a unique-violation-like error.
        if (insertErr.code === '23P01' || /overlap|exclusion/i.test(insertErr.message)) {
          throw new ValidationError('This range overlaps with an existing allocation (same prefix). Pick a different range.')
        }
        if (isRlsRefusal(insertErr)) throw new ValidationError(TOKEN_RANGE_ROLE_REFUSAL)
        throw insertErr
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['run-tokens', run.run_id] })
      qc.invalidateQueries({ queryKey: ['run-token-totals'] })
      setStart(''); setCount(''); setOrderRef(''); setNotes(''); setError(null)
    },
    onError: (e: Error) => setError(e.message),
  })

  const removeAllocation = useMutation({
    mutationFn: async (id: string) => {
      await removeTokenAllocation(supabase as unknown as Parameters<typeof removeTokenAllocation>[0], id)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['run-tokens', run.run_id] })
      qc.invalidateQueries({ queryKey: ['run-token-totals'] })
    },
  })

  return (
    <div className="p-4 space-y-4">
      <MutationOverlays of={[
        [allocate,         'Token range allocated'],
        [removeAllocation, 'Token range removed'],
      ]} />
      {/* Summary */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="card py-3">
          <p className="text-caption text-fg-muted">Allocated</p>
          <p className="text-xl font-bold text-fg tabular-nums">{totalAllocated}</p>
        </div>
        <div className="card py-3">
          <p className="text-caption text-fg-muted">Pallets packed</p>
          <p className="text-xl font-bold text-fg tabular-nums">{palletCount}</p>
        </div>
        <div className="card py-3">
          <p className="text-caption text-fg-muted">Unused</p>
          <p className="text-xl font-bold text-success tabular-nums">
            {Math.max(0, totalAllocated - palletCount)}
          </p>
        </div>
      </div>

      {/* Allocation form — only when run is active */}
      {run.status === 'ACTIVE' && (
        <div className="card space-y-3">
          <p className="text-sm font-semibold text-fg flex items-center gap-2">
            <Ticket size={14} /> Allocate sticker range
          </p>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="label">Prefix</label>
              <input className="field font-mono" placeholder="FM-"
                value={prefix} onChange={e => setPrefix(e.target.value)} />
            </div>
            <div>
              <label className="label">Start #</label>
              <input className="field font-mono" type="number" inputMode="numeric"
                placeholder="10001" value={start} onChange={e => setStart(e.target.value)} />
            </div>
            <div>
              <label className="label">Count</label>
              <input className="field font-mono" type="number" inputMode="numeric"
                placeholder="50" value={count} onChange={e => setCount(e.target.value)} />
            </div>
          </div>
          {startNum > 0 && cnt > 0 && (
            <p className="text-caption text-fg-muted">
              Range: <span className="font-mono text-success">{prefix}{startNum}</span>
              {' → '}
              <span className="font-mono text-success">{prefix}{endNum}</span>
              {' '}({cnt} stickers)
            </p>
          )}

          <div>
            <label className="label">Order reference</label>
            <input className="field" placeholder="Freshmark PO, exporter order..."
              value={orderRef} onChange={e => setOrderRef(e.target.value)} />
          </div>
          <div>
            <label className="label">Notes</label>
            <input className="field" placeholder="(optional)"
              value={notes} onChange={e => setNotes(e.target.value)} />
          </div>

          {error && (
            <div className="rounded-lg bg-danger-soft border border-danger-edge px-3 py-2 text-sm text-danger">
              {error}
            </div>
          )}

          <Button
            variant="primary"
            className="w-full"
            disabled={allocate.isPending || !startNum || !cnt}
            onClick={() => allocate.mutate()}
          >
            {allocate.isPending ? 'Allocating…' : 'Allocate'}
          </Button>
        </div>
      )}

      {/* Existing allocations */}
      <div className="space-y-2">
        <p className="text-caption font-semibold text-fg-muted uppercase tracking-wider">
          Allocations
        </p>
        {allocations.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 text-fg-muted">
            <Ticket size={40} className="mb-2 opacity-40" />
            <p className="text-sm">No sticker ranges allocated yet</p>
          </div>
        )}
        {allocations.map(a => {
          const size = a.range_end - a.range_start + 1
          return (
            <div key={a.allocation_id} className="card space-y-1">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-mono text-fg">
                    {a.token_prefix}{a.range_start} → {a.token_prefix}{a.range_end}
                  </p>
                  <p className="text-caption text-fg-muted mt-0.5">
                    {size} sticker{size === 1 ? '' : 's'}
                    {a.order_reference && <span className="text-fg-muted"> · Order {a.order_reference}</span>}
                  </p>
                  {a.notes && (
                    <p className="text-caption text-fg-muted mt-0.5 italic">{a.notes}</p>
                  )}
                </div>
                {run.status === 'ACTIVE' && (
                  <button
                    onClick={async () => {
                      if ((await confirmDialog({ message: 'Remove this allocation? The sticker range will be freed up.', tone: 'danger' }))) {
                        removeAllocation.mutate(a.allocation_id)
                      }
                    }}
                    className="text-caption text-red-400 hover:text-red-300 flex items-center gap-1 shrink-0"
                  >
                    <Trash2 size={12} /> Remove
                  </button>
                )}
              </div>
              <p className="text-caption text-fg-muted">
                {new Date(a.allocated_at).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })}
              </p>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Grower View preview — embeds the public grower portal so managers can see
// exactly what the grower sees. Read-only, requires verified + share token.
// ---------------------------------------------------------------------------
function GrowerPreview({ run }: { run: Run }) {
  if (!run.verified_at || !run.grower_share_token) {
    return (
      <div className="p-8 flex flex-col items-center justify-center text-center text-fg-muted">
        <ShieldCheck size={48} className="mb-3 opacity-40" />
        <p className="font-medium text-fg-secondary">Not yet verified</p>
        <p className="text-sm mt-1 max-w-xs">
          Use the <span className="text-blue-400">Verify &amp; Publish</span> card on the Overview tab to make this run visible to the grower.
        </p>
      </div>
    )
  }

  const portalUrl = `/g/${run.run_id}?t=${encodeURIComponent(run.grower_share_token)}`
  return (
    <div className="flex flex-col h-full">
      <div className="bg-sky-950/40 border-b border-sky-800 px-4 py-2 flex items-center justify-between text-caption">
        <span className="text-sky-300">
          This is exactly what the grower sees — public, read-only.
        </span>
        <a href={portalUrl} target="_blank" rel="noopener noreferrer"
           className="text-sky-400 hover:text-sky-300 underline">
          Open in new tab ↗
        </a>
      </div>
      <iframe
        src={portalUrl}
        title="Grower portal preview"
        className="flex-1 w-full bg-gray-100 border-0"
        style={{ minHeight: '70vh' }}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Verify & Publish card (FR25 + FR14 + FR26 share)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// CSV export card — one-click pallet-level dump for accounting/grower reports.
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Shrinkage card — converts the tipped-vs-packed mass variance into a Rand
// number so the operational signal lands harder.
// ---------------------------------------------------------------------------
function ShrinkageCard({ run }: { run: Run }) {
  const { data: rates = {} } = useShrinkageRates()
  // resolved guards the one-roundtrip window where the flow defaults to CITRUS
  // and a berry run would flash 'Soft Citrus @ R15/kg' (the original bug).
  const { flow, resolved } = useProductionFlowState()
  const manifest = useFlowManifest(flow)
  const { variance_kg, shrinkage_kg, fruit_type, rate, value_zar, priced: rawPriced } = computeShrinkage(
    {
      tipped_net_weight_kg: run.tipped_net_weight_kg,
      packed_weight_kg:     run.packed_weight_kg,
      reject_weight_kg:     run.reject_weight_kg,
      waste_weight_kg:      run.waste_weight_kg,
      variety:              run.variety,
    },
    rates,
    { usesForceCooling: manifest.usesForceCooling, commodityName: manifest.commodityName },
  )
  const priced = rawPriced && resolved
  const variancePct = run.tipped_net_weight_kg > 0
    ? (Math.abs(variance_kg) / run.tipped_net_weight_kg) * 100
    : 0

  // Negative variance ("surplus") shouldn't be costed — most often it means
  // bin tare was over-stated at tipping. Surface it but don't price it.
  const isSurplus = variance_kg < 0

  return (
    <div className="card space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-caption font-semibold text-fg-muted uppercase tracking-wider">
            Mass reconciliation
          </p>
          <p className="text-caption text-fg-muted">
            Tipped − Packed − Rejects{resolved && <> · {fruit_type}{priced
              ? <> @ <Money value={rate} />/kg</>
              : <> · no R/kg rate set</>}</>}
          </p>
        </div>
        {isSurplus
          ? (
            <span className="text-caption bg-blue-900/40 text-blue-300 rounded-full px-2 py-0.5 font-semibold">
              tipping under-recorded
            </span>
          )
          : (priced ? value_zar > 500 : variancePct > 10) && (
            <span className={clsx('text-caption rounded-full px-2 py-0.5 font-semibold', shrinkageColour(value_zar).replace('text-', 'bg-').replace('-400', '-900/40') + ' ' + shrinkageColour(value_zar))}>
              investigate
            </span>
          )}
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-raised/50 rounded-xl py-2">
          <p className="text-caption text-fg-muted">Variance</p>
          <p className={clsx('font-bold tabular-nums',
            isSurplus ? 'text-blue-400' : value_zar > 500 ? shrinkageColour(value_zar) : 'text-fg'
          )}>
            {variance_kg >= 0 ? '+' : ''}{variance_kg.toFixed(1)} kg
          </p>
          <p className="text-caption text-fg-muted">{variancePct.toFixed(1)}% of tipped</p>
        </div>
        <div className="bg-raised/50 rounded-xl py-2">
          <p className="text-caption text-fg-muted">Shrinkage</p>
          <p className="font-bold text-fg tabular-nums">{shrinkage_kg.toFixed(1)} kg</p>
          <p className="text-caption text-fg-muted">{priced ? 'priced' : 'unpriced'}</p>
        </div>
        <div className={clsx('rounded-xl py-2',
          isSurplus           ? 'bg-blue-950/40' :
          value_zar > 500     ? 'bg-red-950/30'  :
                                'bg-raised/50')}>
          <p className="text-caption text-fg-muted">Estimated value</p>
          <p className={clsx('font-bold tabular-nums',
            isSurplus || !priced ? 'text-fg-muted'   :
                              shrinkageColour(value_zar))}>
            {isSurplus ? '—' : priced ? fmtMoneyAuto(value_zar, { decimals: 0 }) : '—'}
          </p>
          <p className="text-caption text-fg-muted">{priced ? "grower's loss" : resolved ? 'no rate set' : '…'}</p>
        </div>
      </div>

      {!isSurplus && value_zar > 0 && (
        <p className="text-caption text-fg-muted">
          Adjust per-fruit value at <span className="text-blue-400">/config</span> if this looks wrong.
        </p>
      )}
      {!isSurplus && resolved && !priced && shrinkage_kg > 0 && (
        <p className="text-caption text-fg-muted">
          Set a {fruit_type} R/kg value in <span className="text-blue-400">/config</span> to price this.
        </p>
      )}
    </div>
  )
}

function RunExportCard({ run }: { run: Run }) {
  const [busy,  setBusy]  = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function exportPallets() {
    setBusy(true); setError(null)
    try {
      // Union of pallets primary to this run AND build-up pallets where any
      // pallet_lines.source_run_id (mig 191) points at it. Carton + kg are
      // overwritten with this run's line-summed slice; the column
      // `is_buildup_for_this_run` flags rows where this run is a source
      // rather than the primary so the accountant can spot them.
      const linesRes = await supabase.from('pallet_dispatch_lines')
        .select('pallet_id, carton_count, net_mass_kg, run_id')
        .eq('source_run_id', run.run_id)
      if (linesRes.error) throw linesRes.error
      const sliceByPallet = new Map<string, { cartons: number; kg: number; primaryRunId: string }>()
      for (const l of (linesRes.data ?? []) as any[]) {
        const cur = sliceByPallet.get(l.pallet_id) ?? { cartons: 0, kg: 0, primaryRunId: l.run_id }
        cur.cartons += Number(l.carton_count ?? 0)
        cur.kg      += Number(l.net_mass_kg  ?? 0)
        sliceByPallet.set(l.pallet_id, cur)
      }
      const palletIds = Array.from(sliceByPallet.keys())
      const { data, error: err } = palletIds.length === 0
        ? { data: [], error: null }
        : await supabase.from('pallets').select('*')
            .in('pallet_id', palletIds).is('deleted_at', null)
            .order('packed_at')
      if (err) throw err
      const pallets: Record<string, unknown>[] = ((data ?? []) as Record<string, unknown>[]).map(p => {
        const slice = sliceByPallet.get(p.pallet_id as string)
        return {
          ...p,
          carton_count:            slice?.cartons ?? p.carton_count,
          net_weight_kg:           slice?.kg      ?? p.net_weight_kg,
          is_buildup_for_this_run: slice ? slice.primaryRunId !== run.run_id : false,
        }
      })
      const csv = toCSV(
        [
          'pallet_id','status','pallet_type','grade','size_code','pack_type','target_market','destination',
          'ssc','carton_count','gross_weight_kg','net_weight_kg',
          'coldroom_grid','loading_bay_lane','parent_pallet_id',
          'packed_at','stocked_at','inspected_at','dispatched_at',
          'rejection_code','rejection_reason','is_buildup_for_this_run',
        ],
        pallets,
        p => [
          p.pallet_id, p.status, p.pallet_type, p.grade, p.size_code, p.pack_type, p.target_market, p.destination,
          p.ssc_id, p.carton_count, p.gross_weight_kg, p.net_weight_kg,
          p.coldroom_grid, p.loading_bay_lane, p.parent_pallet_id,
          p.packed_at, p.stocked_at, p.inspected_at, p.dispatched_at,
          p.ppecb_rejection_code, p.ppecb_rejection_reason ?? p.rejection_reason,
          p.is_buildup_for_this_run,
        ]
      )
      downloadCSV(`${safeFilename(run.run_number)}-pallets.csv`, csv)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  async function exportRunSummary() {
    setBusy(true); setError(null)
    try {
      // Run summary — one row. Useful for accountants who just want the totals.
      const csv = toCSV(
        [
          'run_number','grower','variety','puc','status',
          'tipped_net_kg','packed_kg','reject_kg','waste_kg','packout_percent','pallet_count',
          'started_at','tipping_stopped_at','completed_at','verified_at',
        ],
        [run],
        r => [
          r.run_number, r.grower, r.variety, r.puc, r.status,
          r.tipped_net_weight_kg, r.packed_weight_kg, r.reject_weight_kg, r.waste_weight_kg,
          r.packout_percent, r.pallet_count,
          r.started_at, r.tipping_stopped_at, r.completed_at, r.verified_at,
        ]
      )
      downloadCSV(`${safeFilename(run.run_number)}-summary.csv`, csv)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card bg-surface border-line space-y-2">
      <p className="text-sm font-semibold text-fg flex items-center gap-2">
        <Download size={14} /> Export
      </p>
      <p className="text-caption text-fg-muted">
        Download CSV files for accounting or grower reports. Opens directly in Excel / Sheets.
      </p>
      <div className="grid grid-cols-2 gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="disabled:opacity-60"
          disabled={busy}
          onClick={exportRunSummary}>
          <Download size={12} /> Run summary
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="disabled:opacity-60"
          disabled={busy}
          onClick={exportPallets}>
          <Download size={12} /> Pallets ({run.pallet_count})
        </Button>
      </div>
      {error && (
        <div className="rounded-lg bg-danger-soft border border-danger-edge px-3 py-2 text-caption text-danger">
          {error}
        </div>
      )}
    </div>
  )
}

// The verified card's "Send link to grower via WhatsApp". WhatsApp is paused
// until the new number is live (Jason, 5 Oct 2026): the button is off while it
// is (and while the status is unknown), and a paused answer is never shown as
// "Sent" — notify-grower used to answer OK having sent nothing, and this card
// ticked "✓ Sent via WhatsApp".
function GrowerLinkWhatsApp({ run, publicLink }: { run: Run; publicLink: string }) {
  const [sending,    setSending]    = useState(false)
  const [sent,       setSent]       = useState(false)
  const [sendErr,    setSendErr]    = useState<string | null>(null)
  const [sendPaused, setSendPaused] = useState(false)
  const wa = useWhatsAppStatus()

  async function sendViaWhatsApp() {
    setSending(true); setSendErr(null); setSendPaused(false)
    try {
      // The grower is keyed by id, never by name (robust across name
      // collisions and renames). The run's own grower_id is set when the run
      // is created; a berry run is built from bins and has NO run_deliveries,
      // so walking only run_deliveries failed every berry send (F02 D4). Older
      // runs without one fall back to run_deliveries → deliveries.grower_id.
      let growerId: string | null = run.grower_id ?? null
      if (!growerId) {
        const { data: linkRow, error: linkErr } = await supabase
          .from('run_deliveries')
          .select('deliveries(grower_id)')
          .eq('run_id', run.run_id)
          .limit(1)
          .maybeSingle()
        // A failed read is not "no grower" — say the lookup failed instead.
        if (linkErr) throw new ValidationError(readErrorText(linkErr))
        const deliveryRow = (linkRow as { deliveries: { grower_id: string | null } | { grower_id: string | null }[] | null } | null)?.deliveries
        growerId = (Array.isArray(deliveryRow) ? deliveryRow[0]?.grower_id : deliveryRow?.grower_id) ?? null
      }

      if (!growerId) throw new ValidationError('No linked grower record — cannot look up WhatsApp number')

      // growers_resolved (mig 271) — picks up the farm-tenant master
      // WhatsApp number when linked, so a notification goes to the
      // farmer's canonical number automatically.
      const { data: grower, error: growerErr } = await supabase
        .from('growers_resolved').select('whatsapp_number, name').eq('grower_id', growerId).maybeSingle()
      if (growerErr) throw new ValidationError(readErrorText(growerErr))
      const phone = grower?.whatsapp_number
      if (!phone) throw new ValidationError(`No WhatsApp number on file for ${grower?.name ?? run.grower}. Add one on the Growers page.`)

      const result = await notifyGrower({
        template:        'PORTAL_LINK',
        phone,
        grower:          run.grower,
        variety:         run.variety,
        packout_percent: run.packout_percent,
        run_number:      run.run_number,
        portal_url:      publicLink,
      })
      if (result.outcome === 'paused') { setSendPaused(true); return }
      if (result.outcome !== 'sent') throw new ValidationError(result.message)
      setSent(true)
      setTimeout(() => setSent(false), 3000)
    } catch (e) {
      setSendErr((e as Error).message)
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      {(wa.paused || sendPaused) && (
        <WhatsAppPausedNote detail="Copy the link above and send it to the grower yourself." />
      )}
      <Button
        variant="primary"
        onClick={sendViaWhatsApp}
        disabled={sending || wa.paused}
        className="w-full disabled:opacity-60"
      >
        {sending ? 'Sending…' : sent ? '✓ Sent via WhatsApp' : '📱 Send link to grower via WhatsApp'}
      </Button>
      {sendErr && (
        <div className="rounded-lg bg-danger-soft border border-danger-edge px-3 py-2 text-caption text-danger">
          {sendErr}
        </div>
      )}
    </>
  )
}

// A verified run's card: the public link, Copy, and the WhatsApp send.
function VerifiedShareCard({ run, verifiedAt, publicLink }: { run: Run; verifiedAt: string; publicLink: string | null }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="card bg-green-950/30 border border-green-700 space-y-3">
      <p className="text-sm font-semibold text-green-300 flex items-center gap-2">
        <ShieldCheck size={16} /> Verified &amp; Published
      </p>
      <p className="text-caption text-green-200/80">
        Verified {new Date(verifiedAt).toLocaleString('en-ZA')}.
        The grower portal link below is read-only and public — share via WhatsApp.
      </p>
      {publicLink && (
        <>
          <div className="bg-canvas rounded-xl px-3 py-2 flex items-center gap-2">
            <Share2 size={14} className="text-fg-muted shrink-0" />
            <code className="text-caption text-fg-secondary flex-1 min-w-0 truncate">{publicLink}</code>
            <button
              onClick={() => {
                navigator.clipboard.writeText(publicLink)
                setCopied(true)
                setTimeout(() => setCopied(false), 1500)
              }}
              className="text-caption text-link hover:text-blue-300 shrink-0 flex items-center gap-1"
            >
              <Copy size={12} /> {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <GrowerLinkWhatsApp run={run} publicLink={publicLink} />
        </>
      )}
    </div>
  )
}

const VERIFY_ISSUE_CLASS = {
  danger:  'rounded-lg bg-danger-soft border border-danger-edge px-3 py-2 text-caption text-danger flex items-start gap-2',
  warning: 'rounded-lg bg-warning-soft border border-warning-edge px-3 py-2 text-caption text-warning flex items-start gap-2',
} as const
/** Blockers (danger) and non-blocking warnings (warning) on the Verify card. */
function VerifyIssueList({ items, tone }: { items: string[]; tone: keyof typeof VERIFY_ISSUE_CLASS }) {
  if (items.length === 0) return null
  return (
    <div className="space-y-1.5">
      {items.map(t => (
        <div key={t} className={VERIFY_ISSUE_CLASS[tone]}>
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <span>{t}</span>
        </div>
      ))}
    </div>
  )
}

function VerifyPublishButton({ roleKnown, canPublish, canVerify, pending, onVerify }: {
  roleKnown: boolean; canPublish: boolean; canVerify: boolean; pending: boolean; onVerify: () => void
}) {
  if (roleKnown && !canPublish) {
    return <p className="text-caption text-fg-muted" data-testid="verify-role-line">{VERIFY_ROLE_LINE}</p>
  }
  return (
    <Button
      variant="primary"
      className="w-full disabled:cursor-not-allowed"
      disabled={!roleKnown || !canVerify || pending}
      onClick={onVerify}
    >
      {pending
        ? 'Verifying…'
        : canVerify
          ? <><ShieldCheck size={16} className="inline mr-1.5" /> Verify &amp; Publish to Grower</>
          : 'Resolve blockers above first'}
    </Button>
  )
}

const VERIFY_ROLE_LINE = 'Only a manager or admin can verify and publish this run.'
const isVerifyRoleRefusal = (m: string) => /Only Managers? \/ Admins? can verify/i.test(m)

export function VerifyCard({
  run, qc,
}: {
  run: Run
  qc: QCSummary & { packer_without_photo: number; titration_warning_no_photo: number }
}) {
  const queryClient = useQueryClient()
  const me = useCurrentUser()
  // UF-10: verify_run accepts the caller's tenant role MANAGER / ADMIN / SUPER_ADMIN only
  // (live body gate, mig 20260724120000). Gate on tenant_role, not role: role carries the
  // sticky SUPER_ADMIN and global fallbacks. While the user read is pending, show the
  // button disabled rather than a wrong "Only a manager" line.
  const tenantRole = me.data?.tenant_role
  // A platform admin resolves to ADMIN inside verify_run (mig 20260724120000), so the
  // server accepts them whatever their active-tenant role is. A pending check counts as
  // not known yet, never as a refusal.
  const platform   = useIsPlatformAdminState()
  const byTenantRole = tenantRole === 'MANAGER' || tenantRole === 'ADMIN' || tenantRole === 'SUPER_ADMIN'
  const canPublish = byTenantRole || platform.isAdmin
  const roleKnown  = me.data != null && (byTenantRole || !platform.isPending)
  const [error,    setError]    = useState<string | null>(null)
  const [token,    setToken]    = useState<string | null>(run.grower_share_token ?? null)

  // Already-verified: show summary + share link
  const verifyCheck = checkVerification({
    status:                     run.status,
    started_at:                 run.started_at,
    completed_at:               run.completed_at,
    packout_percent:            run.packout_percent,
    full_box_count:             qc.full_box,
    packer_problem_count:       qc.packer,
    packer_without_photo:       qc.packer_without_photo,
    titration_warning_count:    qc.warnings,
    titration_warning_no_photo: qc.titration_warning_no_photo,
  })

  const verifyMutation = useMutation({
    mutationFn: async () => {
      setError(null)
      const { data, error: rpcErr } = await withTimeout(supabase.rpc('verify_run', { p_run_id: run.run_id }), 'Runs: verify_run')
      if (rpcErr) throw rpcErr
      const row = Array.isArray(data) ? data[0] : data
      return row as { verified_at: string; grower_share_token: string }
    },
    onSuccess: (row) => {
      setToken(row.grower_share_token)
      queryClient.invalidateQueries({ queryKey: ['runs'] })
    },
    // D2: the server's role refusal is shown once, as one plain sentence (below), not as the
    // error overlay plus the raw red box.
    onError: (e: Error) => setError(isVerifyRoleRefusal(e.message) ? VERIFY_ROLE_LINE : e.message),
  })
  const roleRefused = error === VERIFY_ROLE_LINE

  const publicLink = token
    ? `${window.location.origin}/g/${run.run_id}?t=${encodeURIComponent(token)}`
    : null

  // Already verified — show the share link
  if (run.verified_at) return <VerifiedShareCard run={run} verifiedAt={run.verified_at} publicLink={publicLink} />

  // Not yet verified — show gate
  return (
    <div className="card bg-blue-950/30 border border-blue-700 space-y-3">
      <SubmitOverlay
        isPending={verifyMutation.isPending}
        isSuccess={verifyMutation.isSuccess}
        isError={verifyMutation.isError && !roleRefused}
        error={verifyMutation.error}
        pendingLabel="Verifying…"
        successLabel="Run verified and published"
        onDismiss={() => verifyMutation.reset()}
      />
      <p className="text-sm font-semibold text-blue-300 flex items-center gap-2">
        <ShieldCheck size={16} /> Verify &amp; Publish
      </p>
      <p className="text-caption text-blue-200/80">
        When verified, a read-only link is shared with the grower (packout %, defect photos, totals).
        Raw production data is not exposed.
      </p>

      <VerifyIssueList items={verifyCheck.blockers} tone="danger" />
      <VerifyIssueList items={verifyCheck.warnings} tone="warning" />

      {error && (
        <div className="rounded-lg bg-danger-soft border border-danger-edge px-3 py-2 text-caption text-danger">
          {error}
        </div>
      )}

      <VerifyPublishButton
        roleKnown={roleKnown} canPublish={canPublish} canVerify={verifyCheck.canVerify}
        pending={verifyMutation.isPending} onVerify={() => verifyMutation.mutate()}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Runs Page
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Field-scoped search — same pattern as Deliveries. 'any' looks across all
// the text columns the run card displays so an operator typing a partial
// run number, grower, or variety lands on the right row.
// ---------------------------------------------------------------------------
type RunSearchField =
  | 'any' | 'run_number' | 'grower' | 'variety' | 'puc' | 'commodity'

const RUN_SEARCH_FIELDS: { value: RunSearchField; label: string }[] = [
  { value: 'any',        label: 'Any field' },
  { value: 'run_number', label: 'Run number' },
  { value: 'grower',     label: 'Grower' },
  { value: 'variety',    label: 'Variety' },
  { value: 'puc',        label: 'PUC' },
  { value: 'commodity',  label: 'Commodity' },
]

// Received (prepacked) pallets still staged: they are not runs, but the person
// deciding what to pack next needs to see them from here (plan §2B item 8).
// Reads the receipt runs the register itself filters out.
/** Our OWN fruit staged off a receipt. A storage client's pallets are not ours to
 *  plan a run around (they stay on the register), so the read keeps only receipts
 *  whose ownership is OWN. Paged past the 1,000-row cap, never one clamped range;
 *  a failed page throws so the card says so instead of showing a short count. */
function usePrepackedStaged() {
  const tenantId = useActiveTenantId()
  return useQuery({
    queryKey: ['prepacked-staged', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { rows, error } = await fetchAllPaged<{ carton_count: number | null; run_id: string }>((from, to) =>
        supabase
          .from('pallets')
          .select('pallet_id, carton_count, run_id, runs!inner(run_kind, pallet_receipts!inner(ownership))')
          .in('status', ['IN_PRODUCTION', 'IN_COLDROOM'])
          .is('deleted_at', null)
          .eq('runs.run_kind', 'EXTERNAL_RECEIPT')
          .eq('runs.pallet_receipts.ownership', 'OWN')
          .order('pallet_id')
          .range(from, to) as never)
      if (error) throw new Error(error)
      return { summary: prepackedStagedSummary(rows) }
    },
  })
}

export function PrepackedStagedCard() {
  // G1: the card links to Receive packed pallets, a Deliveries page. A role that does
  // not hold `deliveries` (QC technician) would land on Home, so show the figure as text.
  const canOpenDeliveries = useCanOpenModule('deliveries')
  const q = usePrepackedStaged()
  if (readState(q) === 'unread') {
    return <div className="mb-3"><WidgetRetry noun="staged pallets" onRetry={() => { void q.refetch() }} busy={q.isFetching} /></div>
  }
  const d = q.data?.summary
  if (!d || canOpenDeliveries === null) return null
  const body = (
    <>
      <Package size={18} className="text-cyan-300 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">{d.title}</p>
        <p className="text-caption text-muted">{d.sub}</p>
      </div>
    </>
  )
  if (!canOpenDeliveries) {
    return <div className="card flex items-center gap-3 border border-cyan-800/60 mb-3">{body}</div>
  }
  return (
    <Link to={RECEIPTS_WAITING_PATH}
      className="card flex items-center gap-3 border border-cyan-800/60 hover:border-cyan-600 mb-3">
      {body}
      <span className="text-caption text-cyan-300 shrink-0">Open →</span>
    </Link>
  )
}

const EMPTY_SET: ReadonlySet<string> = new Set()

/** G1: "Go to Tipping" only for a viewer the route gate admits to Tipping
 *  (a QC technician holds Runs but not Tipping); nothing while still deciding. */
export function GoToTippingLink() {
  const navigate = useNavigate()
  const canOpenTipping = useCanOpenModule('tipping')
  if (canOpenTipping !== true) return null
  return (
    <button
      onClick={() => navigate('/tipping')}
      className="text-caption text-link hover:text-blue-300 underline">
      Go to Tipping →
    </button>
  )
}

/** F02 UF-01: "Tip into this run" opens the station on this run. Same gate as
 *  GoToTippingLink; shown on an ACTIVE run only (the station tips onto ACTIVE runs). */
export function TipIntoRunLink({ runId, status, inRow = false }: { runId: string; status: string; inRow?: boolean }) {
  const canOpenTipping = useCanOpenModule('tipping')
  if (canOpenTipping !== true || status !== 'ACTIVE') return null
  const link = (
    <Link to={`/tipping/run/${runId}`} className="btn-secondary btn-sm inline-flex items-center gap-1.5 min-h-[44px]">
      <Truck size={14} /> Tip into this run
    </Link>
  )
  // inRow: the run page's own row, drawn only when the link is (no empty gap).
  return inRow ? <div className="px-4 pt-3 flex flex-wrap gap-2">{link}</div> : link
}

export default function RunsPage() {
  const navigate = useNavigate()
  const qc       = useQueryClient()
  const me       = useCurrentUser()
  const meIsAdmin = isAdmin(me.data?.role)
  // D8: New Run only for the roles the server's runs INSERT accepts.
  const canStartRun = useCanStartRun()
  const canOpenDeliveries = useCanOpenModule('deliveries')
  // U05 UF-03: "Log rejects for this run" only for a viewer the route admits to Rejects.
  const canOpenRejects = useCanOpenModule('rejects')
  // Blueberry runs never link deliveries/bins at create time — fruit arrives
  // as RELEASED cooling batches at tip time (run_batches). So for the berry
  // flow the "empty run" path IS the normal path: open to any run-creator and
  // auto-enabled, with the delivery/bin pickers hidden.
  const flow     = useProductionFlow()
  const manifest = useFlowManifest(flow)
  const isBerry  = manifest.usesForceCooling
  const [creating,       setCreating]       = useState(false)
  const [startedAtOverride, setStartedAtOverride] = useState('')   // admin back-date for new runs
  // Admin-only "empty run" mode — start a run without picking any delivery
  // or stored bin (e.g. ad-hoc / corrective run where intake comes later
  // via the back-fill flow). Grower + variety are mandatory; puc optional.
  const [manualMode,        setManualMode]        = useState(false)
  const [manualGrowerId,    setManualGrowerId]    = useState('')
  const [manualVariety,     setManualVariety]     = useState('')
  const [manualPucId,       setManualPucId]       = useState('')
  // Berry: the no-selection path is the only path — derived, not synced, so it
  // can't flash or go stale (citrus behaviour unchanged: admin opt-in checkbox).
  const effectiveManualMode = manualMode || isBerry
  // PPECB variety_code captured once per run (mig 288). Pallets packed on
  // this run snapshot the code via the pallets_snapshot_variety_code
  // trigger. Strict dropdown from ppecb_varieties cache — empty until
  // the operator has run "Sync PPECB Data" on the Config page.
  // UF-15: null = the operator has not touched the field, so the last run's
  // code may start it (labelled). Any pick, even the blank one, ends that.
  const [varietyCodePick, setVarietyCodePick] = useState<string | null>(null)
  // DUO run (G-08): tag a run with a DUO pack format so its pallets auto-split
  // into two component lines (apply_duo_split). Empty = normal single-commodity
  // run. Registered formats come from Config → Operations → DUO packs; the picker
  // only appears for berry flows that have at least one registered.
  const [duoFormatId, setDuoFormatId] = useState('')
  const { data: duoFormats = [] } = useDuoPackFormats()
  // Linked runs (mig 20261288000000): bulk sort or packing line, and each
  // commodity's run choice (one run per variety, or per commodity = MIXED).
  const linkedOn = useLinkedRunsOn().data === true
  const { data: runGrains } = useRunGrains()
  const [linkedChoice, setLinkedChoice] = useState<LinkedRunChoice>(EMPTY_LINKED_RUN)
  const { data: ppecbVarieties = [] } = useQuery({
    queryKey: ['ppecb-varieties'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ppecb_varieties')
        .select('variety_code, name, commodity_code')
        .order('name')
        // GLOBAL master data (~3100 rows, not tenant-scoped) feeding a variety
        // SELECTION picker — without this the 1000-row cap makes ~2/3 of PPECB
        // varieties unselectable.
        .range(0, 49_999)
      if (error) throw error
      return (data ?? []) as { variety_code: string; name: string; commodity_code: string | null }[]
    },
  })
  // Maps a variety/alias to the canonical commodity (mig 1030) — used so manual
  // runs snapshot a real commodity, never the ambiguous PPECB group code.
  const resolveCommodity = useCommodityResolver()
  // Gate for the commodity-required check below: only enforce when the
  // commodity catalog actually loaded — a cold offline cache would otherwise
  // fail every resolve and block legitimate run creation.
  const { data: commodityCatalog = [] } = useCommodities()
  // The open run is the URL (plan §2D 3): /runs/:runId. Every former
  // setSelectedRunId call navigates, so back and deep links just work.
  const { runId: routeRunId } = useParams<{ runId: string }>()
  const selectedRunId = routeRunId ?? null
  // What the Done panel shows after a successful complete (presentation only).
  // Gone as soon as they open any run.
  const [justCompleted, setJustCompleted] = useState<RunCompleted | null>(null)
  const setSelectedRunId = (id: string | null) => {
    if (id) setJustCompleted(null)
    navigate(id ? `/runs/${id}` : '/runs')
  }
  // F02 UF-02: Back from an open run returns to the page that opened it
  // (the Tipping station) when that page stamped returnTo; a deep link or
  // refresh has none and falls back to the register.
  const returnTo = useReturnTo()
  const leaveRun = () => { setJustCompleted(null); return (returnTo ? navigate(returnTo, { replace: true }) : setSelectedRunId(null)) }
  const [view, setView] = usePersistedState<'cards' | 'table'>('kf49.runs.view', 'cards')
  const [selected,       setSelected]       = useState<Set<string>>(new Set())
  const [selectedBins,   setSelectedBins]   = useState<Set<string>>(new Set())
  const [error,          setError]          = useState<string | null>(null)
  const [searchField,    setSearchField]    = usePersistedState<RunSearchField>('kf49.runs.searchField', 'any')
  // Status chip, search and the 90-day window (M12) — and the link into them:
  // ?status=ACTIVE,TIPPING_COMPLETE&history=all from Home's "Active runs" tile
  // (F16 UF-05; Jason 8 Oct) — the tile counts open runs of any age. Applied before the first register read.
  const {
    statusFilter, setStatusFilter, searchQuery, setSearchQuery, showAllHistory, setShowAllHistory,
  } = useRunsLinkFilters()
  const cutoffIso = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() - 90)
    return d.toISOString()
  }, [])

  // The register read (plan §2D 2). Status and search go to the SERVER and
  // the pages are walked with fetchAllPaged, so "All history" and a search
  // can no longer miss runs past the PostgREST 1,000-row cap. The key keeps
  // the 'runs' prefix every mutation on this page invalidates, and carries
  // the tenant so a tenant switch cannot serve the previous tenant's runs.
  const deferredTerm = useDeferredValue(searchQuery)
  const { data: tenant } = useActiveTenant()
  const tenantId = tenant?.tenantId ?? null
  const runsQ = useQuery({
    queryKey: ['runs', 'register', tenantId, statusFilter, searchField, searchPattern(deferredTerm), showAllHistory ? 'all' : cutoffIso],
    enabled: !!tenantId,
    // U17 UF-07: open-run chips re-read each minute (a stop or complete by
    // someone else shows up without waiting for the 5-minute sweep).
    refetchInterval: registerPollMs(statusFilter),
    queryFn: async () => {
      const pattern = searchPattern(deferredTerm)
      const { rows, error } = await fetchAllPaged<RegisterRow>((from, to) => {
        let q = supabase
          .from('runs').select('*, pack_lines:line_id(name)')
          .is('deleted_at', null)
          // Synthetic external-receipt runs (mig 20260807090000) back pallet
          // receipts, not production — they live on /deliveries/receive.
          .neq('run_kind', 'EXTERNAL_RECEIPT')
          .order('started_at', { ascending: false })
          .range(from, to)
        if (!showAllHistory) q = q.gte('started_at', cutoffIso)
        if (statusFilter === 'VERIFIED') q = q.not('verified_at', 'is', null)
        else {
          const statuses = runStatusesFor(statusFilter)
          if (statuses) q = q.in('status', [...statuses])
          if (statusFilter === 'AWAITING_VERIFY') q = q.is('verified_at', null)
        }
        if (pattern) {
          const orFilter = searchField === 'any' ? anyFieldOr(deferredTerm) : null
          q = orFilter ? q.or(orFilter) : q.ilike(searchField, pattern)
        }
        return q as unknown as PromiseLike<{ data: RegisterRow[] | null; error: { message: string } | null }>
      }, { maxRows: 20_000 })
      if (error) throw new Error(error)
      return rows as RegisterRow[]
    },
  })
  const runs: RegisterRow[] = runsQ.data ?? []
  // U17 D1: Export reads every verified run itself, so the chip, the search and
  // the 90-day window do not decide what is in the file.
  const exportVerified = useMutation({
    mutationFn: async () => {
      const rows = await fetchAllVerifiedRuns()
      if (rows.length === 0) throw new ValidationError('There are no verified runs to export yet.')
      downloadCSV(verifiedRunsFileName(sastTodayYMD()), verifiedRunsCsv(rows))
      return rows.length
    },
  })
  const narrowed = filtersActive(statusFilter, deferredTerm)

  // The KPI strip's read: this week's runs plus every ACTIVE one. Its own key
  // so a filter change never refetches it; withheld (dashes) on a failed read.
  const kpiStart = useMemo(() => kpiWindowStart(new Date()), [])
  const kpiQ = useQuery({
    queryKey: ['runs', 'kpis', tenantId, kpiStart],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('runs').select('status, started_at, tipped_net_weight_kg, intake_kg, packed_weight_kg, pallet_count')
        .is('deleted_at', null)
        .neq('run_kind', 'EXTERNAL_RECEIPT')
        .or(kpiOrFilter(kpiStart))
        .limit(1000)
      if (error) throw error
      // J6: kg tipped counts each fruit once (a line's bulk-sort pallets were
      // counted at the bulk sort).
      return runsKpis(countOnce((data ?? []) as Parameters<typeof runsKpis>[0]), kpiStart)
    },
  })

  // The run page reads its run by id — a deep link outside the window /
  // filter, a COLD cache, and every open run page.
  // ⚠️ It must not wait for the register (F02 D6): that read walks 90 days of
  // runs, and while it did, this query sat disabled — pending with nothing in
  // flight — which QueryStateGate renders as its `empty` verdict. So opening
  // a run on packhouse wifi said "No run with that id — it may have been
  // deleted" until the register landed. Enabled whenever the run page is
  // open, the gate below only ever sees a read in flight or an answer.
  // It is also what keeps the page's figures current (F02 D7): the header and
  // Overview tiles are this row, and nothing refetched it while the page sat
  // open, so a manager watching a live run saw the tipped kg of the moment
  // they opened it. It polls on the standard tier — ONE row by primary key,
  // never the 90-day register — and react-query skips ticks while the tab is
  // hidden. Focus-refetch stays off app-wide (main.tsx): this is the interval.
  const runByIdQ = useQuery({
    queryKey: ['runs', 'by-id', routeRunId ?? null],
    enabled: !!routeRunId,
    refetchInterval: POLL.standard,
    queryFn: async () => {
      const { data, error } = await supabase.from('runs').select('*').eq('run_id', routeRunId!).is('deleted_at', null).maybeSingle()
      if (error) throw error
      return (data as Run | null) ?? null
    },
  })

  // Runs fed by a received (prepacked) pallet — a chip on the card / row.
  const runIdsKey = useMemo(() => runs.map(r => r.run_id).sort().join(','), [runs])
  const prepackedQ = useQuery({
    queryKey: ['runs', 'prepacked-fed', tenantId, runIdsKey],
    enabled: !!tenantId && runs.length > 0 && runs.length <= 500,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('run_lot_sources').select('run_id')
        .in('run_id', runs.map(r => r.run_id))
        .not('source_pallet_id', 'is', null)
      if (error) throw error
      return new Set(((data ?? []) as { run_id: string }[]).map(r => r.run_id)) as ReadonlySet<string>
    },
  })
  // A failed read only costs the 'From prepacked' chip — never a claim.
  const prepackedIds: ReadonlySet<string> = prepackedQ.data !== undefined ? prepackedQ.data : EMPTY_SET

  // Aggregate: total stickers allocated per run — shown on each Run card.
  // M13 — was fetching every packing_token_allocations row in the DB
  // with no filter; at scale that's the whole history of every run on
  // every Runs page open. Scope to the runs currently in the list.
  const visibleRunIds = useMemo(() => runs.map(r => r.run_id), [runs])
  const { data: tokenTotals = {} } = useQuery({
    queryKey: ['run-token-totals', visibleRunIds],
    enabled:  visibleRunIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('packing_token_allocations')
        .select('run_id, range_start, range_end')
        .in('run_id', visibleRunIds)
      if (error) throw error
      const map: Record<string, number> = {}
      for (const a of data as { run_id: string; range_start: number; range_end: number }[]) {
        map[a.run_id] = (map[a.run_id] ?? 0) + (a.range_end - a.range_start + 1)
      }
      return map
    },
  })

  const availableQ = useQuery({
    queryKey: ['deliveries-in-stock'],
    enabled: creating && !isBerry,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deliveries')
        .select('*, delivery_additions(addition_id, bins, added_at)')
        .in('status', ['IN_STOCK', 'RECEIVED'])
        .gt('bins_remaining', 0)
        .is('deleted_at', null)
        .order('received_at')
      if (error) throw error
      return data as Delivery[]
    }
  })
  const { data: available = [], isSuccess: availableLoaded, isError: availableFailed } = availableQ
  // F02 UF-14: drenched loads wait for Mark In Stock before a run can take
  // them; the empty state names them. A failed read says nothing about them.
  const { data: drenchedWaiting } = useQuery({
    queryKey: ['deliveries-drenched-waiting', tenantId],
    enabled: !!tenantId && creating && !isBerry,
    queryFn: async () => {
      const { count, error } = await supabase
        .from('deliveries')
        .select('delivery_id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId!)
        .eq('status', 'DRENCHED')
        .gt('bins_remaining', 0)
        .is('deleted_at', null)
      if (error) throw error
      return count ?? 0
    },
  })
  // F02 UF-14: the stored-bins fold is shut until opened (or a bin is ticked).
  const [binsOpen, setBinsOpen] = useState(false)


  // Stored bins that can be re-tipped into a new run: placed in a coldroom,
  // or stripped off a received pallet and NOT yet placed (REBINNED) — the
  // card says "Not placed" (mig 20261019110000; before it the strip's bin was
  // invisible here until someone placed it).
  // Bins already linked to another ACTIVE run are hidden from the picker (U08 D4):
  // run_bins allows one bin on many runs, so a second link would claim the same
  // fruit. A failed read hides nothing (the bins stay pickable).
  const { data: linkedElsewhere } = useQuery({
    queryKey: ['bins-linked-to-active-runs'],
    enabled: creating && !isBerry,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('run_bins')
        .select('bin_id, runs!inner(status)')
        .eq('runs.status', 'ACTIVE')
      if (error) throw error
      return new Set(((data ?? []) as { bin_id: string }[]).map(r => r.bin_id))
    },
  })
  const { data: allStoredBins = [] } = useQuery({
    queryKey: ['bins-in-coldroom'],
    enabled: creating && !isBerry,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bins')
        .select(`bin_id, bin_number, grower, grower_id, variety, puc, born_date,
                 net_weight_kg, coldroom_id, coldroom_grid, coldroom_overflow, size_code, grade,
                 crate_count, crates_remaining,
                 source_run_id, runs:source_run_id(run_number, started_at),
                 coldrooms:coldroom_id(code)`)
        .in('status', ['IN_COLDROOM', 'REBINNED'])
        .is('deleted_at', null)
        .order('born_date')
      if (error) throw error
      return (data ?? []) as unknown as (StoredBin)[]
    },
  })
  // A bulk-sort pallet can be split between lines (T6, mig 20261289000000),
  // so one reserved by another Active run stays pickable while it has crates.
  const availableBins = linkedElsewhere
    ? allStoredBins.filter(b => !linkedElsewhere.has(b.bin_id) || (b.crates_remaining ?? 0) > 0)
    : allStoredBins

  // F02 UF-03: /runs?new=1&delivery=<id> opens New Run with that delivery
  // ticked. Pristine-only: a touched picker (a delivery or bin ticked, or the
  // empty-run box) is never overwritten. A delivery the picker does not offer
  // (not visible to this reader, or already fully on a run) lands on the plain
  // New Run with a sentence; a failed read says it could not check, never "gone".
  const newRunLink = useNewRunLink(canStartRun, !!me.data)
  const [handedDelivery, setHandedDelivery] = useState<string | null>(null)
  const [handoffNote, setHandoffNote] = useState<string | null>(null)
  const handoffDone = useRef(false)
  // F02 UF-09: ?grower=&variety= (berry) fills the empty-run Grower and Variety,
  // once, and only while both are still untouched.
  const [handedStart, setHandedStart] = useState<{ growerId: string; variety: string } | null>(null)
  useEffect(() => {
    if (newRunLink.openNew) {
      setCreating(true); setHandedDelivery(newRunLink.deliveryId); handoffDone.current = false; setHandoffNote(null)
      setHandedStart(newRunLink.growerId && newRunLink.variety ? { growerId: newRunLink.growerId, variety: newRunLink.variety } : null)
    }
  }, [newRunLink.openNew, newRunLink.deliveryId, newRunLink.nonce])
  useEffect(() => {
    if (!creating || !handedDelivery || handoffDone.current || isBerry) return
    if (availableFailed) {
      handoffDone.current = true
      setHandoffNote('Could not check that delivery. Pick the deliveries for this run below.')
      return
    }
    if (!availableLoaded) return
    handoffDone.current = true
    const found = available.some(d => d.delivery_id === handedDelivery)
    if (!found) {
      setHandoffNote('That delivery is not available for a new run. It may already be on a run. Pick the deliveries below.')
    } else if (selected.size === 0 && selectedBins.size === 0 && !manualMode) {
      setSelected(new Set([handedDelivery]))
    }
  }, [creating, handedDelivery, availableLoaded, availableFailed, available, isBerry]) // eslint-disable-line react-hooks/exhaustive-deps

  const { data: growerList = [] } = useGrowers()
  useEffect(() => {
    if (!creating || !isBerry || !handedStart) return
    if (growerList.length === 0) return   // wait for the growers; the link is kept until they load
    setHandedStart(null)
    if (manualGrowerId || manualVariety) return   // touched: never overwrite
    if (!growerList.some(g => g.grower_id === handedStart.growerId)) return
    setManualGrowerId(handedStart.growerId)
    setManualVariety(handedStart.variety)
  }, [creating, isBerry, handedStart, growerList]) // eslint-disable-line react-hooks/exhaustive-deps
  const waiting = useWaitingDollies(creating && isBerry)

  const selectedDeliveries = available.filter(d => selected.has(d.delivery_id))
  const selectedBinsList   = availableBins.filter(b => selectedBins.has(b.bin_id))
  // Manual-mode pickers (admin path, no delivery/bin selection).
  const { data: pucList    = [] } = usePucs(manualGrowerId || null)
  const manualGrower = growerList.find(g => g.grower_id === manualGrowerId)?.name ?? ''
  const manualPuc    = pucList.find(p => p.puc_id === manualPucId)?.puc_code ?? ''
  // Variety options for the empty-run picker — derive from the chosen
  // grower's registered blocks so admins can't type a variety the grower
  // doesn't actually farm. useGrowers() returns a flat growers_resolved
  // row (no nested PUCs/blocks); useGrowerTree() fetches the nested
  // structure once + caches it across pages, so a second mount of the
  // Runs page after visiting Growers picks the data up instantly.
  // Falls back to a free-text input if the grower has no blocks yet
  // (new tenant on day one).
  const { data: growerTree = [] } = useGrowerTree()
  // Grouped by the fruit of the block that carries each variety, so a grower
  // who farms carrots AND peaches shows the fruit before the pick (UF-01).
  const manualVarietyGroups = useMemo(() => {
    if (!manualGrowerId) return [] as VarietyGroup[]
    const g = growerTree.find(x => x.grower_id === manualGrowerId) as
      | { pucs?: { blocks?: { variety: string | null; commodity: string | null }[] }[] }
      | undefined
    if (!g) return [] as VarietyGroup[]
    return groupVarietiesByFruit((g.pucs ?? []).flatMap(p => p.blocks ?? []))
  }, [growerTree, manualGrowerId])
  const manualVarietyCount = manualVarietyGroups.reduce((n, g) => n + g.varieties.length, 0)
  const firstSelected      = selectedDeliveries[0] ?? (selectedBinsList[0] ? {
    grower:    selectedBinsList[0].grower,
    variety:   selectedBinsList[0].variety,
    puc:       selectedBinsList[0].puc,
    grower_id: selectedBinsList[0].grower_id,
  } : undefined) ?? (effectiveManualMode && manualGrowerId && manualVariety.trim() ? {
    grower:    manualGrower,
    variety:   manualVariety.trim(),
    puc:       manualPuc || null,
    grower_id: manualGrowerId,
  } : undefined)
  // UF-15: the newest run of this variety that carries a PPECB code. A failed
  // read leaves the field empty with no source label (never a false source).
  const lastCodeVariety = firstSelected?.variety?.trim() || ''
  const lastCodeQ = useQuery({
    queryKey: ['runs', 'last-ppecb-code', tenantId, lastCodeVariety],
    enabled: !!tenantId && creating && !!lastCodeVariety,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('runs')
        .select('run_number, variety_code, started_at')
        .eq('tenant_id', tenantId!)
        .eq('variety', lastCodeVariety)
        .is('deleted_at', null)
        .not('variety_code', 'is', null)
        .order('started_at', { ascending: false })
        .limit(1)
      if (error) throw error
      return (data?.[0] ?? null) as { run_number: string; variety_code: string; started_at: string } | null
    },
  })
  const likelyMatches = lastCodeVariety
    ? ppecbVarieties.filter(v => v.name.toLowerCase().includes(lastCodeVariety.toLowerCase()))
    : []
  const lastRun = lastCodeQ.data ?? null
  const codePrefill: { code: string; source: string } | null =
    lastCodeQ.isSuccess && lastRun && ppecbVarieties.some(v => v.variety_code === lastRun.variety_code)
      ? { code: lastRun.variety_code,
          source: `From ${lastRun.run_number} on ${new Date(lastRun.started_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}, the last ${lastCodeVariety} run. Change it if this run differs.` }
      : lastCodeQ.isSuccess && !lastRun && likelyMatches.length === 1
        ? { code: likelyMatches[0].variety_code, source: `The only match for ${lastCodeVariety}. Change it if this run differs.` }
        : null
  const varietyCode = varietyCodePick ?? codePrefill?.code ?? ''
  const varietyCodeSource = varietyCodePick === null ? codePrefill?.source ?? null : null
  // UF-02: the run's fruit and whether it has anything packable. Both modes;
  // the warning never blocks Create. The fruit is resolved from the picked
  // variety only (manual: the PPECB variety name, then the typed variety;
  // otherwise the first picked delivery or bin's variety). Create prefers the
  // block's own commodity in delivery mode (and the source run's for bins),
  // which is read at save time, so the two can differ on a re-tagged block.
  const hasPickedSource = selected.size > 0 || selectedBins.size > 0
  const warnCommodity = effectiveManualMode && !hasPickedSource
    ? (resolveCommodity(ppecbVarieties.find(v => v.variety_code === varietyCode)?.name) ?? resolveCommodity(manualVariety))
    : resolveCommodity(firstSelected?.variety)
  const warnFormats = useCommodityPackFormats(warnCommodity)
  const warnRules = useActiveProductRules(creating)
  const flowForWarn = useEntityFlowResolverByName()
  const warnWeighs = useFlowManifest(flowForWarn(warnCommodity)).tracksPalletWeight
  const fruitHasNothingPackable = noPackableCode({
    commodity: warnCommodity,
    formatsLoaded: warnFormats.isSuccess,
    rulesLoaded: warnRules.loaded,
    formatCodes: (warnFormats.data ?? []).map(f => f.code),
    codesWithRateCard: warnRules.codesWithRateCard,
    weighs: warnWeighs,
  })
  const totalSelectedBins  = selectedDeliveries.reduce((sum, d) => sum + d.bins_remaining, 0)
                           + selectedBinsList.length

  function toggleDelivery(id: string) {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  function toggleBin(id: string) {
    setSelectedBins(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const createRun = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession(); const user = session?.user
      if (!user) throw new ValidationError('Not authenticated')
      // Refuse BEFORE generate_run_number: that RPC has no role check, so a
      // refused insert used to burn a run number (the role changed while the
      // picker was open — the button is hidden otherwise).
      if (!canStartRun) throw new ValidationError(RUN_CREATE_REFUSED)

      const hasSelections = selected.size > 0 || selectedBins.size > 0
      // Empty runs: admin opt-in for citrus; the standard path for berry
      // (cooling batches attach at tip time via run_batches).
      const isManual = effectiveManualMode && (meIsAdmin || isBerry) && !hasSelections
      if (!isManual && !hasSelections) {
        throw new ValidationError('Select at least one delivery or stored bin')
      }
      if (isManual) {
        if (!manualGrowerId) throw new ValidationError('Pick a grower')
        if (!manualVariety.trim()) throw new ValidationError('Type the variety')
      }

      // Rule: a run must contain exactly one grower + one variety — across
      // BOTH deliveries and bins. Catches accidental cross-contamination of
      // a packing batch and keeps billing attribution unambiguous. Skipped
      // when the run is admin-empty (no selections at all).
      const selectedDeliveries = available.filter(d => selected.has(d.delivery_id))
      const selectedBinsList   = availableBins.filter(b => selectedBins.has(b.bin_id))
      if (hasSelections) {
        const growers   = new Set([
          ...selectedDeliveries.map(d => d.grower.trim()),
          ...selectedBinsList.map(b => b.grower.trim()),
        ])
        if (growers.size > 1) {
          throw new ValidationError(`A run can only contain one grower. You picked ${growers.size}: ${Array.from(growers).join(', ')}`)
        }
        // The one-variety rule moved below the commodity lookup: a commodity
        // set to one run per commodity (linked runs, mig 20261288000000) takes
        // every variety, and a DUO run (G-08) draws from both its fruits.
      }
      const pickedVarieties = [
        ...selectedDeliveries.map(d => d.variety),
        ...selectedBinsList.map(b => b.variety),
      ]

      const grower    = firstSelected!.grower
      const variety   = firstSelected!.variety
      const puc       = firstSelected!.puc ?? null
      const growerId  = firstSelected!.grower_id ?? null

      // Snapshot commodity from the first delivery's block, or — if the
      // run is bin-only — from the source run of the first bin. Stays put
      // even if the block's commodity is later edited (historical accuracy).
      // In manual mode, resolve the picked variety to a real commodity via the
      // alias map (mig 1030) — never the ambiguous PPECB GROUP code, e.g. "CI"
      // (all citrus) or "GR" (grapes), which corrupts pricing + dispatch (#2).
      let commodity: string | null = null
      const firstBlockId = (selectedDeliveries[0] as Delivery | undefined)?.block_id ?? null
      if (firstBlockId) {
        const { data: blk } = await supabase
          .from('blocks').select('commodity').eq('block_id', firstBlockId).maybeSingle()
        commodity = (blk?.commodity as string | null) ?? null
      } else if (selectedBinsList[0]?.source_run_id) {
        const { data: srcRun } = await supabase
          .from('runs').select('commodity').eq('run_id', selectedBinsList[0].source_run_id).maybeSingle()
        commodity = (srcRun?.commodity as string | null) ?? null
      } else if (isManual) {
        const ppecbVar = ppecbVarieties.find(v => v.variety_code === varietyCode)
        commodity = resolveCommodity(ppecbVar?.name) ?? resolveCommodity(variety) ?? null
      }

      // Every path falls back to resolving the variety itself (a delivery
      // whose block has no commodity, a bin whose source run predates the
      // column). A run may NOT be created without a commodity: NULL
      // runs.commodity hides line presets and rate-card variants downstream
      // (Anchor FRONTERAS run, 2026-07-14 — variant-matching onboarding plan,
      // Phase 2). Skipped when the catalog hasn't loaded (cold offline cache)
      // so a missing lookup table never blocks the intake line.
      commodity = commodity ?? resolveCommodity(variety)
      if (!commodity && commodityCatalog.length > 0) {
        throw new ValidationError(
          `Variety "${variety}" doesn't resolve to a commodity. Add it as a variety under Commodities, or set the commodity on the source block, then create the run. Runs without a commodity hide line presets and rate-card pack variants.`,
        )
      }
      const runVariety = newRunVariety({
        grain: grainFor(runGrains, commodity), duo: !!duoFormatId, picked: pickedVarieties, first: variety,
      })
      if ('error' in runVariety) throw new ValidationError(runVariety.error)
      const isMixed = runVariety.variety === MIXED_VARIETY

      // Use the DB-side sequence-backed generator — never collides even if runs
      // are deleted or multiple users hit Create simultaneously.
      const { data: numData, error: numErr } = await withTimeout(supabase.rpc('generate_run_number'), 'Runs: generate_run_number')
      if (numErr) throwFriendly(numErr)
      const runNumber = numData as string

      // Admin-only back-date. Empty = use server NOW(). Non-today date
      // anchors started_at at noon SAST on that day so the run lands on
      // the chosen calendar date for FIFO + reporting.
      const todayLocal = sastTodayYMD()
      // Same reasoning as the delivery back-date: the input's max= is a hint,
      // and `runs` has no server-side future-date guard.
      if (meIsAdmin && startedAtOverride && startedAtOverride > todayLocal) {
        throw new ValidationError('Run start date cannot be in the future.')
      }
      const startedAtIso = (meIsAdmin && startedAtOverride && startedAtOverride !== todayLocal)
        ? `${startedAtOverride}T12:00:00+02:00`
        : null

      const { data: run, error: runErr } = await withTimeout(supabase
        .from('runs')
        .insert({
          run_number: runNumber,
          grower, grower_id: growerId, variety: runVariety.variety, puc, commodity,
          // Mig 288 — PPECB variety_code captured at run-start. Pallets
          // snapshot from this via the BEFORE-INSERT trigger. A run of every
          // variety has no single code.
          variety_code: isMixed ? null : (varietyCode || null),
          // Linked runs (mig 20261288000000): bulk sort or packing line, and
          // the line's product and feed. Nothing when linked runs are off.
          ...linkedRunInsertFields(linkedOn, linkedChoice),
          // DUO run marker (G-08). NULL = normal run; set = its pallets auto-split.
          duo_format_id: duoFormatId || null,
          started_by: user.id,
          ...(startedAtIso ? { started_at: startedAtIso } : {}),
        })
        .select('run_id').single(), 'Runs: runs insert')
      if (runErr) throwFriendly(runErr, RUN_CREATE_DOMAIN)

      // These are three sequential writes with no transaction. A timeout or a
      // transient failure on either link insert used to leave a COMMITTED runs
      // row that had already consumed a sequence-backed run number but had no
      // deliveries or bins attached — the operator saw an error, retried, and
      // burned a second number, leaving a phantom run on the board. Undo the
      // parent instead so a retry is clean.
      try {
        if (selected.size > 0) {
          const links = [...selected].map(delivery_id => ({ run_id: run.run_id, delivery_id }))
          const { error: linkErr } = await withTimeout(supabase.from('run_deliveries').insert(links), 'Runs: run_deliveries insert')
          if (linkErr) throw linkErr
        }

        if (selectedBins.size > 0) {
          const binLinks = [...selectedBins].map(bin_id => ({ run_id: run.run_id, bin_id }))
          const { error: binErr } = await withTimeout(supabase.from('run_bins').insert(binLinks), 'Runs: run_bins insert')
          if (binErr) throw binErr
        }
      } catch (linkFail) {
        // Best effort: the run has no children yet by definition, so this is a
        // clean delete. If it fails too, say so — a stranded numbered run is
        // something an operator needs to know about rather than discover later.
        const { error: undoErr } = await withTimeout(
          supabase.from('runs').delete().eq('run_id', run.run_id), 'Runs: undo orphan run')
        if (undoErr) {
          throw new Error(
            `Run ${runNumber} was created but its deliveries/bins could not be attached, and the empty run could not be removed. Tell an admin before starting another. (${(linkFail as Error).message} / ${undoErr.message})`,
          )
        }
        throw linkFail
      }
      return run.run_id as string
    },
    onSuccess: (newRunId?: string) => {
      qc.invalidateQueries({ queryKey: ['runs'] })
      qc.invalidateQueries({ queryKey: ['deliveries-in-stock'] })
      qc.invalidateQueries({ queryKey: ['bins-in-coldroom'] })
      setCreating(false)
      setSelected(new Set())
      setSelectedBins(new Set())
      setStartedAtOverride('')
      setVarietyCodePick(null)
      setDuoFormatId('')
      setLinkedChoice(EMPTY_LINKED_RUN)
      setManualMode(false)
      setManualGrowerId('')
      setManualVariety('')
      setManualPucId('')
      setError(null)
      setHandedDelivery(null)
      setHandoffNote(null)
      // F02 UF-03: Create lands on the new run, where Tip into this run is the next step.
      if (newRunId) navigate(`/runs/${newRunId}`)
    },
    onError: (e: Error) => setError(friendlyError(e, RUN_CREATE_DOMAIN).message)
  })

  // The lifecycle writes live in useRunLifecycle so the run page, Table rows
  // and the Tipping station header run exactly what the register cards run.
  const lifecycle = useRunLifecycle({
    canOpenRejects,
    onCompleted: r => {
      // UF-05: the run page shows the Done panel too (RunDetail), the register otherwise.
      setJustCompleted({
        run_id: r.run_id, run_number: r.run_number,
        pallet_count: r.pallet_count, packed_weight_kg: r.packed_weight_kg,
      })
    },
    onReopenError: e => setError(e.message),
  })
  const { stopTipping, reopenForTipping, completeRun } = lifecycle

  // ── RUN DETAIL ──
  if (selectedRunId) {
    // The run's own (polled) read wins; the register's row paints the page
    // until it lands, so opening from the register is still instant.
    const run = runByIdQ.data ?? runs.find(r => r.run_id === selectedRunId) ?? null
    if (run) return <RunDetail run={run} onBack={leaveRun} lifecycle={lifecycle} done={justCompleted} onDismissDone={() => setJustCompleted(null)} />
    return (
      <div className="min-h-full bg-canvas">
        <PageHeader onBack={leaveRun} tabTitle="Run" />
        <div className="p-4">
          <QueryStateGate query={runByIdQ} noun="run"
            empty={<p className="text-sm text-muted">No run with that id — it may have been deleted, or the link is from another business.</p>} />
        </div>
      </div>
    )
  }

  // ── CREATE RUN ──
  if (creating) {
    const groups = available.reduce<Record<string, Delivery[]>>((acc, d) => {
      const key = `${d.grower} — ${d.variety}`
      if (!acc[key]) acc[key] = []
      acc[key].push(d)
      return acc
    }, {})

    // One flag for the fixed create bar AND the scroller clearance below it —
    // the bar stacks summary + PPECB select + back-date + Create (~300px), far
    // taller than the old static pb-32, so the last cards were untappable
    // (mobile audit 2026-07-19, S4).
    const createBarVisible = selected.size > 0 || selectedBins.size > 0
      || (effectiveManualMode && !!manualGrowerId && !!manualVariety.trim())

    return (
      <div className="min-h-full bg-canvas flex flex-col">
        <PageHeader onBack={() => { setCreating(false); setHandedDelivery(null); setHandoffNote(null); setSelected(new Set()); setSelectedBins(new Set()); setError(null) }}
          title="New Run" />

        <div className={clsx('flex-1 p-4 space-y-4 overflow-y-auto', createBarVisible ? 'pb-96' : 'pb-32')}>
          {/* The instruction opens the body (PW-13): a header subtitle is one
              truncating line, and on a phone that cut "same grower + variety",
              the rule the picker enforces. */}
          {handoffNote && <p role="status" className="text-sm text-warning">{handoffNote}</p>}
          <p className="text-sm text-fg-muted">
            {isBerry
              ? 'Pick the grower + variety — released cooling batches are tipped in once the run is live'
              : 'Pick deliveries and/or stored bins · same grower + variety'}
          </p>
          {/* Citrus: admin-only opt-in to start a run without any delivery /
              stored bin (ad-hoc / corrective, intake back-filled later).
              Berry: this IS run creation — batches attach at tip time. */}
          {(meIsAdmin || isBerry) && (
            <div className={clsx('card space-y-2', isBerry ? 'border border-line' : 'border border-amber-700/40 bg-amber-900/10')}>
              {!isBerry && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox"
                    className="w-4 h-4 rounded border-line"
                    checked={manualMode}
                    onChange={e => {
                      setManualMode(e.target.checked)
                      if (e.target.checked) {
                        // Clear any prior list-selection so the form-side
                        // fields are the only source of truth.
                        setSelected(new Set())
                        setSelectedBins(new Set())
                      }
                    }} />
                  <span className="text-sm font-semibold text-amber-200">
                    Start an empty run (admin)
                  </span>
                </label>
              )}
              {effectiveManualMode && (
                <div className="space-y-2 pt-1">
                  <p className={clsx('text-caption', isBerry ? 'text-fg-muted' : 'text-amber-200/80')}>
                    {isBerry
                      ? 'Grower + variety are required. Force-cooled batches are linked when you tip them into this run.'
                      : 'No delivery or bin attached — intake gets back-filled later. Grower + variety are required.'}
                  </p>
                  {isBerry && (
                    <div className="space-y-1" data-testid="waiting-dollies">
                      <span className="text-caption text-fg-muted block">Released dollies with no run yet</span>
                      {waiting.loading && <p className="text-caption text-fg-muted">Checking the released dollies…</p>}
                      {waiting.failed && <p role="status" className="text-caption text-warning">Could not check the released dollies. Pick the grower and variety below.</p>}
                      {!waiting.loading && !waiting.failed && waiting.groups.length === 0 && (
                        <p className="text-caption text-fg-muted">Every released dolly already has an active run.</p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {waiting.groups.map(g => {
                          const gid = g.growerId ?? growerList.find(x => x.name === g.grower)?.grower_id ?? ''
                          return (
                            <button key={g.key} type="button" disabled={!gid}
                              className="min-h-[44px] px-3 rounded-lg border border-line bg-surface text-sm text-fg text-left disabled:opacity-50"
                              onClick={() => { setManualGrowerId(gid); setManualPucId(''); setManualVariety(g.variety) }}>
                              {g.grower} · {g.variety} · {g.count} {g.count === 1 ? 'dolly' : 'dollies'}
                            </button>
                          )
                        })}
                      </div>
                      {waiting.groups.length > 0 && <p className="text-caption text-fg-muted">Tap one to fill Grower and Variety. You can still change both.</p>}
                    </div>
                  )}
                  <label className="block">
                    <span className="text-caption text-fg-muted block mb-1">Grower</span>
                    <select className="w-full bg-surface text-fg text-sm rounded-lg p-2 border border-line"
                      value={manualGrowerId}
                      onChange={e => { setManualGrowerId(e.target.value); setManualPucId(''); setManualVariety('') }}>
                      <option value="">— Pick a grower —</option>
                      {growerList.map(g => (
                        <option key={g.grower_id} value={g.grower_id}>{g.name}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-caption text-fg-muted block mb-1">
                      Variety
                      {manualGrowerId && manualVarietyCount === 0 && (
                        <span className="text-warning ml-1">· no blocks on this grower, type freehand</span>
                      )}
                    </span>
                    {manualVarietyCount > 0 ? (
                      <select
                        className="w-full bg-surface text-fg text-sm rounded-lg p-2 border border-line"
                        value={manualVariety}
                        disabled={!manualGrowerId}
                        onChange={e => setManualVariety(e.target.value)}>
                        <option value="">— Pick a variety —</option>
                        {manualVarietyGroups.length > 1
                          ? manualVarietyGroups.map(g => (
                              <optgroup key={g.fruit ?? 'other'} label={fruitLabel(g.fruit) ?? 'Other'}>
                                {g.varieties.map(v => <option key={v} value={v}>{v}</option>)}
                              </optgroup>
                            ))
                          : manualVarietyGroups.flatMap(g => g.varieties).map(v => (
                              <option key={v} value={v}>{v}</option>
                            ))}
                      </select>
                    ) : (
                      <input type="text"
                        className="w-full bg-surface text-fg text-sm rounded-lg p-2 border border-line disabled:opacity-50"
                        placeholder={manualGrowerId ? 'e.g. Nova / Tango / Valencia' : 'Pick a grower first'}
                        disabled={!manualGrowerId}
                        value={manualVariety}
                        onChange={e => setManualVariety(e.target.value)} />
                    )}
                  </label>
                  <label className="block">
                    <span className="text-caption text-fg-muted block mb-1">
                      PUC <span className="text-fg-muted">(optional)</span>
                    </span>
                    <select className="w-full bg-surface text-fg text-sm rounded-lg p-2 border border-line"
                      value={manualPucId}
                      disabled={!manualGrowerId}
                      onChange={e => setManualPucId(e.target.value)}>
                      <option value="">{manualGrowerId ? '— None —' : 'Pick a grower first'}</option>
                      {pucList.map(p => (
                        <option key={p.puc_id} value={p.puc_id}>{p.puc_code}</option>
                      ))}
                    </select>
                  </label>
                </div>
              )}
            </div>
          )}

          {linkedOn && <LinkedRunFields value={linkedChoice} onChange={setLinkedChoice} />}

          {/* DUO run (G-08). Berry-only, and only when the packhouse has
              registered DUO packs (Config → Operations → DUO packs). Tagging the
              run makes every pallet on it split into two component lines by the
              format's ratio; the single-variety intake rule relaxes so both
              fruits can be tipped in. */}
          {isBerry && duoFormats.length > 0 && (
            <div className="card space-y-2 border border-indigo-700/40 bg-indigo-900/10">
              <label className="block">
                <span className="text-sm font-semibold text-indigo-200 flex items-center gap-1.5">
                  <Combine size={15} /> DUO run (mixed-fruit punnets)
                </span>
                <p className="text-caption text-indigo-200/70 mt-0.5 mb-1.5">
                  Optional. Pick a DUO pack to make this a mixed run — its pallets carry
                  both fruits as separate traceable lines, split by the pack's ratio.
                </p>
                <select className="w-full bg-surface text-fg text-sm rounded-lg p-2 border border-line"
                  value={duoFormatId}
                  onChange={e => setDuoFormatId(e.target.value)}>
                  <option value="">Normal run (single fruit)</option>
                  {duoFormats.map(f => (
                    <option key={f.duo_format_id} value={f.duo_format_id}>
                      {f.code}{f.label ? ` — ${f.label}` : ''}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}

          {available.length === 0 && !effectiveManualMode && (
            <QueryStateGate query={availableQ} noun="deliveries" empty={
              <div className="flex flex-col items-center justify-center py-16 text-fg-muted">
                <Package size={48} className="mb-3 opacity-40" />
                <p>No deliveries available</p>
                <p className="text-sm mt-1 text-center">Deliveries must be In Stock or Received before they can be added to a run</p>
                {!!drenchedWaiting && (
                  <p className="text-sm mt-2 text-center">
                    {drenchedWaiting} drenched {drenchedWaiting === 1 ? 'delivery is' : 'deliveries are'} waiting for Mark In Stock.{' '}
                    {canOpenDeliveries === true && (
                      <Link to="/deliveries" className="underline text-accent inline-flex items-center min-h-[44px]">Open Deliveries</Link>
                    )}
                  </p>
                )}
              </div>
            } />
          )}

          {Object.entries(groups).map(([groupLabel, deliveries]) => (
            <div key={groupLabel}>
              <p className="text-caption font-semibold text-fg-muted uppercase tracking-wider mb-2 px-1">
                {groupLabel}
              </p>
              <div className="space-y-2">
                {deliveries.map(d => {
                  const isSelected = selected.has(d.delivery_id)
                  const loadCount  = d.delivery_additions?.length ?? 1
                  // Client ref isn't on the shared Delivery type yet; pulled
                  // through by the `select *` query (mig 144 added the column).
                  const clientRef = (d as Delivery & { client_ref_no?: string | null }).client_ref_no ?? null
                  return (
                    <button key={d.delivery_id}
                      onClick={() => toggleDelivery(d.delivery_id)}
                      className={clsx(
                        'w-full text-left rounded-xl p-4 border-2 transition-colors flex items-center gap-3',
                        isSelected ? 'border-green-500 bg-green-900/20' : 'border-line bg-surface hover:border-gray-600'
                      )}>
                      <div className={clsx(
                        'w-6 h-6 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors',
                        isSelected ? 'border-green-500 bg-green-500' : 'border-line'
                      )}>
                        {isSelected && <Check size={14} className="text-fg" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {d.delivery_note_number && (
                            <span className="text-caption font-mono bg-raised text-fg rounded-lg px-1.5 py-0.5">
                              DN {d.delivery_note_number}
                            </span>
                          )}
                          <p className="font-semibold text-fg">{d.grower}</p>
                          {clientRef && (
                            <span className="text-caption font-mono bg-blue-900/40 text-blue-300 rounded-lg px-1.5 py-0.5">
                              {clientRef}
                            </span>
                          )}
                          <span className={clsx('text-caption rounded-full px-2 py-0.5 font-medium',
                            d.status === 'IN_STOCK' ? 'bg-green-900/50 text-green-400' : 'bg-raised text-fg-secondary')}>
                            {deliveryStatusLabel(d.status)}
                          </span>
                        </div>
                        <p className="text-sm text-fg-muted">
                          {d.variety}{d.block ? ` · Block ${d.block}` : ''}{d.puc ? ` · PUC ${d.puc}` : ''}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-bold tabular-nums text-fg">{d.bins_remaining}</p>
                        <p className="text-caption text-fg-muted">bins</p>
                        {loadCount > 1 && <p className="text-caption text-blue-400 mt-0.5">{loadCount} loads</p>}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          ))}

          {/* Stored bins — re-tip from cold storage. Filtered to the same
              grower+variety as the current selection so the one-grower-one-
              variety rule is respected at pick time. Grouped by source run,
              then by sort (size + class) so packers can quickly grab all of
              a particular sort. */}
          {availableBins.length > 0 && (() => {
            const visibleBins = availableBins.filter(b => {
              if (!firstSelected) return true
              return b.grower.trim() === firstSelected.grower.trim()
                  && b.variety.trim() === firstSelected.variety.trim()
            })
            if (visibleBins.length === 0) return null

            // Group by source run, then by sort (size+class). Track the
            // run's started_at so we can sort oldest-first (FIFO — get the
            // oldest stored fruit out the door first).
            const byRun = new Map<string, { runNumber: string; startedAt: string; bins: StoredBin[] }>()
            for (const b of visibleBins) {
              const key = b.source_run_id
              if (!byRun.has(key)) byRun.set(key, {
                runNumber: b.runs?.run_number ?? key.slice(0, 8),
                startedAt: b.runs?.started_at ?? b.born_date,
                bins: [],
              })
              byRun.get(key)!.bins.push(b)
            }
            // Sort runs by start date ascending — oldest first, so the
            // longest-stored fruit is the first thing the operator sees.
            const runGroups = Array.from(byRun.entries())
              .map(([runId, info]) => ({ runId, ...info }))
              .sort((a, b) => a.startedAt.localeCompare(b.startedAt))

            const open = binsOpen || selectedBins.size > 0
            return (
              <div>
                <button type="button" onClick={() => setBinsOpen(!open)}
                  aria-expanded={open} aria-controls="stored-bins-panel"
                  className="w-full min-h-[44px] px-3 rounded-xl border border-line bg-surface text-left flex items-center gap-2">
                  <ChevronDown size={14} aria-hidden="true" className={clsx('transition-transform shrink-0', !open && '-rotate-90')} />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-cyan-400">Stored bins ({visibleBins.length})</span>
                    <span className="block text-caption text-fg-muted">Re-tip from cold storage. They follow the grower and variety you pick first.</span>
                  </span>
                </button>
                <div id="stored-bins-panel" hidden={!open} className="space-y-3 mt-2">
                  {runGroups.map(rg => {
                    // Sub-group by sort label
                    const bySort = new Map<string, StoredBin[]>()
                    for (const b of rg.bins) {
                      const sortLabel = sortLabelFor(b)
                      if (!bySort.has(sortLabel)) bySort.set(sortLabel, [])
                      bySort.get(sortLabel)!.push(b)
                    }
                    const sortGroups = Array.from(bySort.entries())
                      .sort(([a], [b]) => a.localeCompare(b))

                    const allInRun = rg.bins.every(b => selectedBins.has(b.bin_id))
                    const someInRun = rg.bins.some(b => selectedBins.has(b.bin_id))

                    return (
                      <div key={rg.runId} className="bg-surface/30 rounded-xl border border-line-subtle overflow-hidden">
                        {/* Run header — bulk-toggle all in run */}
                        <button
                          onClick={() => {
                            setSelectedBins(prev => {
                              const next = new Set(prev)
                              if (allInRun) {
                                rg.bins.forEach(b => next.delete(b.bin_id))
                              } else {
                                rg.bins.forEach(b => next.add(b.bin_id))
                              }
                              return next
                            })
                          }}
                          className="w-full px-3 py-2 flex items-center gap-2 hover:bg-surface/60 text-left">
                          <div className={clsx(
                            'w-5 h-5 rounded border-2 shrink-0 flex items-center justify-center',
                            allInRun
                              ? 'border-cyan-500 bg-cyan-500'
                              : someInRun
                                ? 'border-cyan-500 bg-cyan-500/30'
                                : 'border-line'
                          )}>
                            {allInRun && <Check size={12} className="text-fg" />}
                            {!allInRun && someInRun && <span className="text-cyan-200 text-caption">−</span>}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-mono font-semibold text-fg text-sm">
                              {rg.runNumber}
                              <span className="text-fg-muted font-sans font-normal ml-1.5 text-caption">
                                · {rg.bins.length} bin{rg.bins.length === 1 ? '' : 's'}
                              </span>
                            </p>
                            <p className="text-caption text-fg-muted font-sans">
                              packed {new Date(rg.startedAt).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                              {' · '}
                              {Math.floor((Date.now() - new Date(rg.startedAt).getTime()) / 86_400_000)}d ago
                            </p>
                          </div>
                        </button>

                        {/* Sort sub-groups */}
                        {sortGroups.map(([sortLabel, sortBins]) => {
                          const allInSort = sortBins.every(b => selectedBins.has(b.bin_id))
                          return (
                            <div key={sortLabel} className="border-t border-line-subtle/60">
                              <button
                                onClick={() => {
                                  setSelectedBins(prev => {
                                    const next = new Set(prev)
                                    if (allInSort) {
                                      sortBins.forEach(b => next.delete(b.bin_id))
                                    } else {
                                      sortBins.forEach(b => next.add(b.bin_id))
                                    }
                                    return next
                                  })
                                }}
                                className="w-full px-4 py-1.5 flex items-center gap-2 hover:bg-surface/40 text-left">
                                <div className={clsx(
                                  'w-4 h-4 rounded border shrink-0 flex items-center justify-center',
                                  allInSort ? 'border-cyan-400 bg-cyan-500' : 'border-line'
                                )}>
                                  {allInSort && <Check size={10} className="text-fg" />}
                                </div>
                                <p className="text-caption text-fg-secondary flex-1">
                                  {sortLabel}
                                  <span className="text-fg-muted ml-1.5">
                                    · {sortBins.length} bin{sortBins.length === 1 ? '' : 's'}
                                  </span>
                                </p>
                              </button>

                              {/* Individual bins */}
                              <div className="px-4 pb-2 space-y-1">
                                {sortBins.map(b => {
                                  const isSelected = selectedBins.has(b.bin_id)
                                  const ageDays = Math.floor((Date.now() - new Date(b.born_date).getTime()) / 86_400_000)
                                  return (
                                    <button key={b.bin_id}
                                      onClick={() => toggleBin(b.bin_id)}
                                      className={clsx(
                                        'w-full text-left rounded-lg px-2.5 py-2 border transition-colors flex items-center gap-2',
                                        isSelected
                                          ? 'border-cyan-500 bg-cyan-900/20'
                                          : 'border-line bg-canvas/40 hover:border-gray-600'
                                      )}>
                                      <div className={clsx(
                                        'w-4 h-4 rounded-full border shrink-0 flex items-center justify-center',
                                        isSelected ? 'border-cyan-400 bg-cyan-500' : 'border-line'
                                      )}>
                                        {isSelected && <Check size={10} className="text-fg" />}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="font-mono text-caption text-cyan-300 truncate">{b.bin_number}</p>
                                        <p className="text-caption text-fg-muted truncate">
                                          {b.coldrooms?.code && cellOrOverflow(b)
                                            ? `${b.coldrooms.code}/${cellOrOverflow(b)}`
                                            : 'Not placed'}
                                          {' · '}
                                          {ageDays}d old
                                        </p>
                                      </div>
                                      <div className="text-right shrink-0">
                                        <p className="text-sm font-bold text-fg tabular-nums">
                                          {Math.round(b.net_weight_kg)}
                                          <span className="text-caption text-fg-muted font-normal ml-0.5">kg</span>
                                        </p>
                                      </div>
                                    </button>
                                  )
                                })}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })()}

        </div>

        {createBarVisible && (
          <div className="fixed bottom-0 left-0 right-0 z-30 above-bottom-nav bg-canvas border-t border-line px-4 pt-4 pb-[max(env(safe-area-inset-bottom),1rem)] space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-fg-muted">
                {effectiveManualMode && selected.size === 0 && selectedBins.size === 0 ? (
                  <>Empty run · {manualGrower} · {manualVariety.trim()}</>
                ) : (
                  <>
                    {selected.size > 0 && `${selected.size} deliver${selected.size > 1 ? 'ies' : 'y'}`}
                    {selected.size > 0 && selectedBins.size > 0 && ' · '}
                    {selectedBins.size > 0 && `${selectedBins.size} stored bin${selectedBins.size > 1 ? 's' : ''}`}
                  </>
                )}
              </span>
              {(selected.size > 0 || selectedBins.size > 0) && (
                <span className="text-fg font-bold tabular-nums">{totalSelectedBins} bins total</span>
              )}
            </div>
            {/* PPECB variety_code — strict picker from synced ppecb_varieties
                (mig 288). One choice per run; pallets inherit via trigger.
                Pre-filters to varieties whose PPECB name matches the run's
                friendly variety text (case-insensitive substring), with the
                full list available too. */}
            <div>
              <label htmlFor="run-ppecb-code" className="text-caption text-fg-muted block mb-1">
                PPECB variety code
                {firstSelected?.variety && (
                  <span className="text-fg-muted ml-1">(run variety: {firstSelected.variety})</span>
                )}
              </label>
              <select
                id="run-ppecb-code"
                className="w-full bg-surface text-fg text-sm rounded-lg p-2 border border-line"
                value={varietyCode}
                onChange={e => setVarietyCodePick(e.target.value)}>
                <option value="">— Select (required for PPECB submission) —</option>
                {ppecbVarieties.length === 0 ? (
                  <option value="" disabled>(empty — Sync PPECB Data on Config page first)</option>
                ) : (
                  <>
                    {firstSelected?.variety && (
                      <optgroup label={`Likely matches for "${lastCodeVariety}"`}>
                        {ppecbVarieties
                          .filter(v => firstSelected.variety && v.name.toLowerCase().includes(firstSelected.variety.toLowerCase()))
                          .map(v => (
                            <option key={`m-${v.variety_code}`} value={v.variety_code}>
                              {v.name} ({v.variety_code})
                            </option>
                          ))}
                      </optgroup>
                    )}
                    <optgroup label="All varieties">
                      {ppecbVarieties.map(v => (
                        <option key={v.variety_code} value={v.variety_code}>
                          {v.name} ({v.variety_code}){v.commodity_code ? ` · ${v.commodity_code}` : ''}
                        </option>
                      ))}
                    </optgroup>
                  </>
                )}
              </select>
              {varietyCodeSource && (
                <p className="text-caption text-fg-muted mt-1" data-testid="ppecb-code-source">{varietyCodeSource}</p>
              )}
              {!varietyCode && (
                <p className="text-caption text-warning mt-1">
                  Leave blank only if this run won't be PPECB-submitted (e.g. local market).
                </p>
              )}
              {/* Manual runs derive their commodity from the variety (mig 1030).
                  Surface it so the operator confirms a real commodity rather
                  than the run landing with none (the mig-867 "Comm: —" bug). */}
              {effectiveManualMode && selected.size === 0 && selectedBins.size === 0 && (() => {
                const resolved = resolveCommodity(ppecbVarieties.find(v => v.variety_code === varietyCode)?.name)
                  ?? resolveCommodity(manualVariety)
                return (
                  <p className={`text-caption mt-1 ${resolved ? 'text-fg-muted' : 'text-warning'}`}>
                    {resolved
                      ? <>Commodity: <span className="text-fg font-medium">{resolved}</span></>
                      : <>⚠ This variety doesn't match a fruit. Pick a registered variety, or add this one under Commodities first.</>}
                  </p>
                )
              })()}
              {fruitHasNothingPackable && (
                <div className="bg-warning-soft border border-warning-edge rounded-xl p-3 text-caption text-warning mt-2">
                  <p className="font-semibold">No {fruitLabel(warnCommodity)} pack type has a rate card yet.</p>
                  <p>No pallet can be packed on this run. Check the variety, or ask an admin to add the rate cards.</p>
                  {/* UF-06: one request per code (noPackableCode is false until the card read succeeded). */}
                  <div className="flex flex-wrap gap-2 mt-2">
                    <MissingCardRequests codes={(warnFormats.data ?? []).map(f => f.code)}
                      fruit={warnCommodity} fruitName={fruitLabel(warnCommodity)} />
                  </div>
                </div>
              )}
            </div>

            {/* Admin-only back-date for the run. Empty = server NOW(). */}
            {meIsAdmin && (
              <div>
                <label className="text-caption text-fg-muted block mb-1">
                  Started on <span className="text-fg-muted">(admin can back-date)</span>
                </label>
                <input type="date"
                  className="w-full bg-surface text-fg text-sm rounded-lg p-2 border border-line"
                  max={sastTodayYMD()}
                  value={startedAtOverride}
                  onChange={e => setStartedAtOverride(e.target.value)} />
                {startedAtOverride && startedAtOverride !== sastTodayYMD() && (
                  <p className="text-caption text-warning mt-1">
                    Back-dated — the run's start time will be {startedAtOverride} 12:00 SAST.
                  </p>
                )}
              </div>
            )}
            {error && <p className="text-sm text-danger">{error}</p>}
            <Button variant="primary" className="w-full" disabled={createRun.isPending} onClick={() => createRun.mutate()}>
              {createRun.isPending
                ? 'Creating…'
                : (effectiveManualMode && selected.size === 0 && selectedBins.size === 0)
                  ? '✓ Create empty run'
                  : `✓ Create Run · ${totalSelectedBins} bins`}
            </Button>
          </div>
        )}
      </div>
    )
  }

  // ── RUN LIST ──
  return (
    <div className="min-h-full bg-canvas flex flex-col">
      {/* The lifecycle writes used to say nothing at all — stopTipping and
          completeRun did not even carry an onError, so a refused transition
          left the button looking untapped and the run in its old state. */}
      <MutationOverlays of={[
        [createRun,         'Run created'],
        [stopTipping,       'Tipping stopped'],
        [reopenForTipping,  'Run reopened for tipping'],
        [completeRun,       'Run completed'],
        [exportVerified,    'Verified runs exported'],
      ]} />
      {/* PW-13: actions, not children — on a phone they take their own row,
          so "Production Runs" is no longer clipped beside them (PM-SH-05). */}
      <PageHeader title="Production Runs"
        actions={<>
          <Button aria-label="Export CSV of all verified runs"
            variant="ghost"
            size="sm"
            onClick={() => exportVerified.mutate()}
            title="Download every verified run, all history, as a CSV"
            disabled={exportVerified.isPending}>
            <Download size={14} />
            {exportVerified.isPending ? 'Exporting…' : 'Export verified'}
          </Button>
          {canStartRun && (
            <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
              <Plus size={16} /> New Run
            </Button>
          )}
        </>} />

      {/* Runs that received a variety they don't declare. Renders nothing when
          every run is clean, so citrus never sees it. */}
      <RunVarietyMismatchBanner runs={runs} />
      {/* Linked runs (T2): a bulk sort still open from an earlier week. */}
      <StaleBulkSortBanner onOpen={id => setSelectedRunId(id)} />
      {/* Linked runs (T9): the day's or week's map of how the runs link. */}
      <RunMap />
      <PrepackedStagedCard />
      {justCompleted && (
        <RunCompleteDone done={justCompleted}
          onVerify={() => setSelectedRunId(justCompleted.run_id)}
          onDismiss={() => setJustCompleted(null)} />
      )}

      {/* Field-scoped search */}
      <div className="px-4 py-2 border-b border-line-subtle bg-canvas/40 flex items-center gap-2">
        <select
          value={searchField}
          onChange={e => setSearchField(e.target.value as RunSearchField)}
          className="bg-surface border border-line rounded-lg px-2 py-1.5 text-caption text-fg shrink-0">
          {RUN_SEARCH_FIELDS.map(f => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
        <div className="relative flex-1">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-muted pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder={searchField === 'any'
              ? 'Search runs…'
              : `Search ${RUN_SEARCH_FIELDS.find(f => f.value === searchField)?.label.toLowerCase()}…`}
            className="w-full bg-surface border border-line rounded-lg pl-7 pr-7 py-1.5 text-caption text-fg placeholder-fg-muted"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-0 top-1/2 -translate-y-1/2 min-w-[44px] min-h-[44px] flex items-center justify-center text-fg-muted hover:text-fg-secondary"
              aria-label="Clear search">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Status filter chips */}
      <div className="flex gap-2 px-4 py-3 overflow-x-auto scrollbar-hide border-b border-line-subtle items-center">
        {RUN_STATUS_CHIPS.map(f => (
          <button key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className={clsx(
              'shrink-0 rounded-full px-3 py-1.5 text-caption font-semibold transition-colors',
              statusFilter === f.value
                ? 'bg-accent text-on-fill'
                : 'bg-raised text-fg-secondary hover:bg-gray-600'
            )}>
            {f.label}
          </button>
        ))}
        <button
          onClick={() => setShowAllHistory(v => !v)}
          title={showAllHistory ? 'Showing all history' : 'Showing last 90 days only'}
          className={clsx(
            'ml-auto shrink-0 rounded-full px-3 py-1.5 text-caption font-semibold transition-colors',
            showAllHistory ? 'bg-amber-600 text-on-fill' : 'bg-surface text-fg-secondary hover:bg-raised'
          )}>
          {showAllHistory ? 'All history' : 'Last 90 days'}
        </button>
        <PalletViewToggle value={view} onChange={setView} />
      </div>

      {/* The strip is independent of the filters: what the floor is doing
          now and did this week, whatever the list below is narrowed to. */}
      <div className="px-4 pt-4"><RunsKpiStrip q={kpiQ} /></div>

      {(() => {
        // Status and search are applied on the server (see runsQ); the rows
        // here are already the answer.
        const filteredRuns = runs

        return (
          <div className="flex-1 p-4 space-y-3">
            {/* Gate the empty branch — pending/paused/error must never render
                "No runs yet" as truth (idle-wake audit UI-4). */}
            {runs.length === 0 && !narrowed && (
              <QueryStateGate query={runsQ} noun="runs" skeleton={{ rows: 4, rowClassName: 'h-28 rounded-xl' }}
                empty={
                  <TeachingEmptyState icon={Package} status="No runs yet"
                    cue={canStartRun
                      ? 'Start a run from the deliveries waiting, then tip fruit into it.'
                      : 'A manager, scale operator or admin starts the run.'}
                    action={canStartRun ? (
                      <Button variant="primary" onClick={() => setCreating(true)}>Start a run</Button>
                    ) : undefined} />
                } />
            )}

            {runsQ.isSuccess && runs.length === 0 && narrowed && (
              <div className="flex flex-col items-center justify-center py-16 text-fg-muted text-center">
                <Package size={48} className="mb-3 opacity-40" />
                <p>No runs match the current filter</p>
                <button
                  onClick={() => { setStatusFilter('ALL'); setSearchQuery('') }}
                  className="text-caption text-link hover:text-blue-300 underline mt-2">
                  Clear filters
                </button>
              </div>
            )}

            {view === 'table' && filteredRuns.length > 0 && (
              <RunsTable rows={filteredRuns} prepacked={prepackedIds} onOpen={id => setSelectedRunId(id)}
                actions={isManagerOrAbove(me.data?.role)
                  ? { isAdmin: meIsAdmin, busy: lifecycle.busy, onAction: (kind, id) => {
                      const run = runs.find(x => x.run_id === id)
                      if (run) lifecycle.act(kind, run)
                    } }
                  : undefined} />
            )}

            {view === 'cards' && filteredRuns.map(r => (
          <button key={r.run_id}
            className="card w-full text-left space-y-3 hover:border-line border-2 border-transparent transition-colors"
            onClick={() => setSelectedRunId(r.run_id)}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-semibold text-fg">{r.run_number}</p>
                <p className="text-sm text-fg-muted">{r.grower} · {r.variety}</p>
                {r.puc && <p className="text-caption text-fg-muted mt-0.5">PUC {r.puc}</p>}
                <p className="text-caption text-fg-muted mt-0.5">{runCells(r, fmtDateZA).startedLine}</p>
                <RunChips run={r} prepacked={prepackedIds} />
                {tokenTotals[r.run_id] > 0 && (
                  <p className="text-caption text-blue-400 mt-0.5 flex items-center gap-1">
                    <Ticket size={10} /> {tokenTotals[r.run_id]} sticker{tokenTotals[r.run_id] === 1 ? '' : 's'} allocated
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <StatusPill object="run" status={r.status} label={runStatusLabel(r.status)} />
                {r.verified_at && <StatusPill object="run" status="VERIFIED" />}
                <ChevronRight size={16} className="text-fg-muted" />
              </div>
            </div>

            <PackoutTiles run={r} />

            {/* Run lifecycle actions — state machine + admin-only reopen guards
                live in runActions (src/lib/runStatus.ts); the COMPLETE reopen is
                the higher-friction "danger" confirm because verification /
                invoicing downstream may already have kicked in. */}
            {isManagerOrAbove(me.data?.role) && <RunActionButtons
              status={r.status as RunStatus}
              runNumber={r.run_number}
              isAdmin={meIsAdmin}
              isManager
              canArchive={canArchiveRun(r)}
              busy={lifecycle.busy}
              onAction={kind => lifecycle.act(kind, r)}
            />}
          </button>
            ))}
          </div>
        )
      })()}
    </div>
  )
}

// =============================================================================
// QC summary card on the Run overview tab. Folds the legacy log counts
// and the dynamic-engine pass/fail breakdown into one strip so a manager
// can see the QC story without leaving the run.
// =============================================================================

interface RunDynamicQcSummary {
  templates: { template_code: string; template_name: string; total: number; pass: number; fail: number; conditional: number; in_progress: number }[]
  defects:   { code: string; count: number }[]
  total:     number
  completed: number
  fail:      number
  conditional: number
}

function RunQcSummaryCard({ legacy, dynamic, onOpenQcTab }: {
  legacy:  { full_box: number; packer: number; titration: number; warnings: number; avgBrix: number | null; avgAcid: number | null } | undefined
  dynamic: RunDynamicQcSummary | undefined
  onOpenQcTab: () => void
}) {
  const legacyTotal = (legacy?.full_box ?? 0) + (legacy?.packer ?? 0) + (legacy?.titration ?? 0)
  const dynamicTotal = dynamic?.total ?? 0
  const noActivity = legacyTotal === 0 && dynamicTotal === 0
  if (noActivity) return null

  const failBadge =
    (dynamic?.fail ?? 0) > 0
      ? { tone: 'bg-red-900/40 text-red-300 border-red-700/40',     label: `${dynamic!.fail} fail` }
    : (dynamic?.conditional ?? 0) > 0
      ? { tone: 'bg-amber-900/40 text-amber-300 border-amber-700/40', label: `${dynamic!.conditional} conditional` }
    : null

  return (
    <button onClick={onOpenQcTab}
      className="card p-3 w-full text-left hover:bg-raised/50 transition-colors space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <p className="text-caption uppercase tracking-wider text-fg-muted font-semibold">Quality control</p>
          <p className="text-sm text-fg-secondary">
            {legacyTotal + dynamicTotal} check{legacyTotal + dynamicTotal === 1 ? '' : 's'}
            {failBadge && (
              <span className={clsx('ml-2 text-caption uppercase tracking-wide rounded-full px-2 py-0.5 border', failBadge.tone)}>
                {failBadge.label}
              </span>
            )}
            {(legacy?.warnings ?? 0) > 0 && (
              <span className="ml-2 text-caption uppercase tracking-wide bg-amber-900/40 text-amber-300 rounded-full px-2 py-0.5 border border-amber-700/40">
                {legacy!.warnings} low-acid warning{legacy!.warnings === 1 ? '' : 's'}
              </span>
            )}
          </p>
        </div>
        <span className="text-fg-muted text-caption shrink-0">View →</span>
      </div>

      {/* Legacy counts as compact pills */}
      {legacyTotal > 0 && (
        <div className="flex items-center gap-3 text-caption">
          {(legacy?.full_box ?? 0) > 0 && <span className="text-fg-muted">Full box · <span className="text-fg font-semibold">{legacy?.full_box}</span></span>}
          {(legacy?.packer    ?? 0) > 0 && <span className="text-fg-muted">Packer · <span className="text-fg font-semibold">{legacy?.packer}</span></span>}
          {(legacy?.titration ?? 0) > 0 && <span className="text-fg-muted">Titration · <span className="text-fg font-semibold">{legacy?.titration}</span></span>}
          {legacy?.avgBrix != null && <span className="text-fg-muted">Brix · <span className="text-emerald-300 font-semibold">{legacy.avgBrix.toFixed(1)}</span></span>}
          {legacy?.avgAcid != null && <span className="text-fg-muted">Acid · <span className={clsx('font-semibold', legacy.avgAcid <= 0.6 ? 'text-amber-300' : 'text-emerald-300')}>{legacy.avgAcid.toFixed(2)}</span></span>}
        </div>
      )}

      {/* Dynamic per-template result strip */}
      {dynamic && dynamic.templates.length > 0 && (
        <div className="space-y-1.5">
          {dynamic.templates.slice(0, 4).map(t => {
            const completed = t.pass + t.fail + t.conditional
            const passPct   = completed > 0 ? Math.round((t.pass / completed) * 100) : null
            return (
              <div key={t.template_code}>
                <div className="flex items-baseline justify-between gap-2 mb-0.5 text-caption">
                  <p className="text-fg-secondary truncate">{t.template_name}</p>
                  <p className="text-fg-muted tabular-nums shrink-0">
                    {t.total} run{t.total === 1 ? '' : 's'}
                    {passPct != null && <span className="ml-2 text-emerald-300 font-semibold">{passPct}% pass</span>}
                  </p>
                </div>
                {completed > 0 && (
                  <div className="flex h-1.5 rounded-full overflow-hidden bg-surface">
                    <div className="bg-emerald-500" style={{ width: `${(t.pass / completed) * 100}%` }} />
                    <div className="bg-amber-500"   style={{ width: `${(t.conditional / completed) * 100}%` }} />
                    <div className="bg-red-500"     style={{ width: `${(t.fail / completed) * 100}%` }} />
                  </div>
                )}
              </div>
            )
          })}
          {dynamic.templates.length > 4 && (
            <p className="text-caption text-fg-muted">…and {dynamic.templates.length - 4} more in the QC tab.</p>
          )}
        </div>
      )}
    </button>
  )
}

// QC-tab subsection: per-template results + top defects for this run.
function RunDynamicQcCard({ dynamic }: { dynamic: RunDynamicQcSummary }) {
  const { labelOf } = useDefectLabelMap()
  return (
    <div className="card space-y-3">
      <p className="text-caption text-fg-muted font-semibold uppercase tracking-wide">Dynamic checks</p>
      {dynamic.templates.map(t => {
        const completed = t.pass + t.fail + t.conditional
        const passPct   = completed > 0 ? Math.round((t.pass / completed) * 100) : null
        return (
          <div key={t.template_code}>
            <div className="flex items-baseline justify-between gap-2 mb-1 text-sm">
              <p className="text-fg truncate">{t.template_name}</p>
              <p className="text-caption text-fg-muted tabular-nums shrink-0">
                {t.total} run{t.total === 1 ? '' : 's'}
                {passPct != null && <span className="ml-2 text-emerald-300 font-semibold">{passPct}% pass</span>}
              </p>
            </div>
            {completed > 0 && (
              <div className="flex h-2 rounded-full overflow-hidden bg-surface">
                <div className="bg-emerald-500" style={{ width: `${(t.pass / completed) * 100}%` }} />
                <div className="bg-amber-500"   style={{ width: `${(t.conditional / completed) * 100}%` }} />
                <div className="bg-red-500"     style={{ width: `${(t.fail / completed) * 100}%` }} />
              </div>
            )}
            <div className="flex gap-3 text-caption text-fg-muted mt-1">
              <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1" />{t.pass} pass</span>
              <span><span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-1" />{t.conditional} conditional</span>
              <span><span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-1" />{t.fail} fail</span>
              {t.in_progress > 0 && <span className="text-fg-muted">· {t.in_progress} in progress</span>}
            </div>
          </div>
        )
      })}

      {dynamic.defects.length > 0 && (
        <div className="border-t border-line pt-3 space-y-1.5">
          <p className="text-caption uppercase tracking-wider text-fg-muted">Defects on this run</p>
          {dynamic.defects.slice(0, 8).map(d => {
            const max = dynamic.defects[0].count
            const pct = (d.count / max) * 100
            return (
              <div key={d.code}>
                <div className="flex justify-between text-caption mb-0.5">
                  <span className="text-fg-secondary truncate">{labelOf(d.code)}</span>
                  <span className="text-fg-muted tabular-nums">{d.count}</span>
                </div>
                <div className="h-1.5 bg-surface rounded-full overflow-hidden">
                  <div className="h-full bg-red-500" style={{ width: `${pct}%` }} />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
